import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaArrowRight, FaSync, FaLayerGroup, FaSearch,
  FaFolder, FaFolderOpen, FaTrash,
  FaCheck, FaTimes, FaArrowUp, FaArrowDown, FaFolderPlus, FaCamera,
  FaUndo, FaRedo, FaSearchPlus, FaSearchMinus, FaCompress, FaCopy,
  FaMapMarkerAlt, FaSave
} from "react-icons/fa";
import { API_BASE } from "../api/config";
import { cleanFilenameForTrello, buildTrelloTitle } from "../utils/trelloUtils";
import useVerschiebenJobs from "../utils/useVerschiebenJobs";
import VerschiebenJobIndicator from "../components/VerschiebenJobIndicator";
import EnginePicker from "../components/EnginePicker";
import useDragPanel from "../utils/useDragPanel";
import { dragOutProps, contextShareProps } from "../utils/dragOut";
import "./BilderVerschiebenPage.css";

/* == API Helper == */
const api = (path, opts = {}) =>
  fetch(API_BASE + "/zuordnen/api" + path, {
    headers: { "Content-Type": "application/json", ...opts.headers },
    ...opts,
  }).then(async (r) => {
    if (!r.ok) {
      let detail = "";
      try { const j = await r.json(); detail = j.detail || ""; } catch {}
      throw new Error("API " + r.status + (detail ? ": " + detail : ""));
    }
    return r.json();
  });

const apiPost = (path, body) =>
  fetch(API_BASE + "/zuordnen/api" + path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(async (r) => {
    if (!r.ok) {
      let detail = "";
      try { const j = await r.json(); detail = j.detail || ""; } catch {}
      throw new Error("API " + r.status + (detail ? ": " + detail : ""));
    }
    return r.json();
  });

const apiPostDirect = (path, body) =>
  fetch(API_BASE + path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(async (r) => {
    if (!r.ok) {
      let detail = "";
      try { const j = await r.json(); detail = j.detail || ""; } catch {}
      throw new Error("API " + r.status + (detail ? ": " + detail : ""));
    }
    return r.json();
  });

/* == Confidence Styles == */
const CONF = {
  green:   { bg: "#0d3320", border: "#2e7d32", text: "#81c784", label: "Sicher",      icon: "●" },
  yellow:  { bg: "#2e2a0d", border: "#f9a825", text: "#fff176", label: "Möglich",     icon: "◐" },
  red:     { bg: "#3b1010", border: "#c62828", text: "#ef9a9a", label: "Unbekannt",   icon: "○" },
  white:   { bg: "#1a1a2e", border: "#e0e0e0", text: "#ffffff", label: "Nicht in DB", icon: "◇" },
  auto:    { bg: "#0d2e2e", border: "#00bcd4", text: "#4dd0e1", label: "Auto",        icon: "⚡" },
  manuell: { bg: "#2d1a40", border: "#9c27b0", text: "#ce93d8", label: "Manuell",     icon: "M" },
  none:    { bg: "#1a1a2e", border: "#444",    text: "#888",    label: "",             icon: "" },
};

const GROUP_ORDER = [
  { key: "auto",    label: "Auto",        icon: "⚡" },
  { key: "green",   label: "Sicher",      icon: "●" },
  { key: "yellow",  label: "Möglich",     icon: "◐" },
  { key: "red",     label: "Unbekannt",   icon: "○" },
  { key: "white",   label: "Nicht in DB", icon: "◇" },
  { key: "manuell", label: "Manuell",     icon: "M" },
  { key: "none",    label: "Ohne Status", icon: "–" },
];

const MANUELL_ZIEL = "/Holthey Immobilien/--- scan - Ablage/##Rename-me Batch/Batch Input/Zähler/##Manuell";
const BILDER_ZIEL = "/Holthey Immobilien/--- scan - Ablage/##Rename-me Batch/Batch Input/Bilder";

const ART_OPTIONS = ["Strom", "Gas", "Wasser", "Heizung", "Öl", "Allgemein", "Zwischenzähler"];
// Welle 80: Zählerstand immer als Ganzzahl (Ingvar: „was hinter dem Punkt ist, brauche ich nicht")
const ganz = (v) => {
  if (v == null || v === "") return "";
  const f = parseFloat(String(v).replace(",", "."));
  return Number.isNaN(f) ? String(v) : String(Math.floor(f));
};

/* Auto degradiert zu green wenn Stand fehlt (Backend setzt auto nur wenn Stand vorhanden) */
function _effectiveConf(s) {
  const conf = s?.confidence || "none";
  if (conf === "auto" && !s.stand) return "green";
  return conf;
}

/* Finde den origIdx der ersten Datei nach visueller Gruppenreihenfolge */
function _firstVisualIdx(files, settings) {
  for (const g of GROUP_ORDER) {
    const idx = files.findIndex((d) => _effectiveConf(settings[d.path]) === g.key);
    if (idx >= 0) return idx;
  }
  return files.length > 0 ? 0 : -1;
}

function BilderVerschiebenPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Phasen: loading → ready → analysing → analysed
  const [phase, setPhase] = useState("loading");
  const [loadMsg, setLoadMsg] = useState("Zählerstände laden...");
  const [error, setError] = useState(null);
  const [dateien, setDateien] = useState([]);
  const [inbox, setInbox] = useState("");
  const [analysed, setAnalysed] = useState(false);
  const autoAnalyseDone = useRef(false);

  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbLoading, setThumbLoading] = useState(false);
  const thumbCache = useRef({});
  const [fileSettings, setFileSettings] = useState({});  // {[path]: {prefix, art, stand, einheit, zaehlernummer, foto_datum, ziel_ordner, confidence, reason, kopie_nok, dateiname_neu}}
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browsePath, setBrowsePath] = useState("");
  const [browseItems, setBrowseItems] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveResult, setMoveResult] = useState(null);
  const [showBatch, setShowBatch] = useState(false);
  const [batchMoving, setBatchMoving] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [autoMoving, setAutoMoving] = useState(false);
  const [sourceFolder, setSourceFolder] = useState(location.state?.ordner || "");
  const [modus] = useState(location.state?.modus || "verschieben"); // "verschieben" | "umbenennen"
  const [nurDateien] = useState(location.state?.nurDateien || null); // optional: nur bestimmte Dateien laden
  const [checkedPaths, setCheckedPaths] = useState(new Set());

  /* == Duplikat-Check State == */
  const [duplikatInfo, setDuplikatInfo] = useState(null);   // { quell, existierend, onDelete, onAbort }
  const [duplikatQuellThumb, setDuplikatQuellThumb] = useState(null);  // { src } für Inline-Vorschau
  const [duplikatZoom, setDuplikatZoom] = useState(null);               // src für Zoom-Overlay
  const [duplikatChecking, setDuplikatChecking] = useState(false);
  const [duplikatList, setDuplikatList] = useState([]);     // für Batch: [{quell, existierend}]
  const [duplikatWarnungen, setDuplikatWarnungen] = useState({});  // {path: {existierend, duplikat_typ}} — Früh-Warnungen
  const [editingPath, setEditingPath] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [confirmDeletePath, setConfirmDeletePath] = useState(null);
  const [duplicatingPath, setDuplicatingPath] = useState(null);
  const [ordnerFehltModal, setOrdnerFehltModal] = useState(null); // { pfad, datei }
  const [inFlightPaths, setInFlightPaths] = useState(new Set()); // Dateien in laufenden Verschiebe-Jobs
  const [jobVorlauf, setJobVorlauf] = useState(""); // z. B. "Duplikat-Prüfung läuft…" vor dem Job-Start
  const { dragOffset: browseDrag, onDragStart: onBrowseDragStart } = useDragPanel(); // Ordner-Browser verschiebbar
  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [lockedGroups, setLockedGroups]       = useState({});  // Gruppe eingefroren bei Analyse → kein Springen beim Bearbeiten
  const [katalogEinheiten, setKatalogEinheiten] = useState([]);  // Prefix-Dropdown-Daten (aus zaehler_katalog)
  const [anlegenModal, setAnlegenModal] = useState(null);  // { path, zaehlernummer } für "Neuen Zähler anlegen" Dialog
  const [zaehlerSuchResults, setZaehlerSuchResults] = useState([]);  // Suchergebnisse vom Katalog
  const [zaehlerSuchPath, setZaehlerSuchPath] = useState(null);      // Pfad des Bildes mit offener Suche
  const [obisFreitext, setObisFreitext] = useState({});              // { [path]: true } wenn Freitext-OBIS-Eingabe aktiv
  const [zaehlerArten, setZaehlerArten] = useState(ART_OPTIONS);    // Alle zaehler_art aus DB
  const listRef = useRef(null);
  const cardRefs = useRef({});
  const browseTargetPath = useRef(null);
  const dateienRef = useRef(dateien);
  dateienRef.current = dateien;
  const [scrollTick, setScrollTick] = useState(0);   // force scroll-into-view after list changes

  /* == Image Viewer: Rotation + Zoom == */
  const [imgRotation, setImgRotation] = useState(0);
  const [thumbRefresh, setThumbRefresh] = useState(0);
  const [imgZoom, setImgZoom] = useState(1);
  const [imgPan, setImgPan] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panOffset = useRef({ x: 0, y: 0 });
  const zaehlerSuchDebounceRef = useRef(null);

  /* == Auto-Save: Track previous selection for save-on-change == */
  const prevSelectedIdx = useRef(-1);
  const savingRef = useRef(false);

  /* == Keyboard-Navigation Refs == */
  const standInputRefs = useRef({});
  const moveBtnRefs = useRef({});
  const duplikatReplaceRef = useRef(null);
  const trelloBeimVerschiebenRef = useRef(null);

  /* == Trello Popup State == */
  const [trelloPopupOpen, setTrelloPopupOpen] = useState(false);
  const [trelloPopupPath, setTrelloPopupPath] = useState(null);
  const [trelloGrund, setTrelloGrund] = useState("");
  const [trelloTitel, setTrelloTitel] = useState("");
  const [trelloSaving, setTrelloSaving] = useState(false);
  const [trelloError, setTrelloError] = useState("");
  const [trelloSuccess, setTrelloSuccess] = useState("");

  /* == NOK-Picker Popup == */
  const [nokPickerFile, setNokPickerFile] = useState(null);

  /* == Filter == */
  const [filterArt, setFilterArt] = useState("");
  const [filterPrefix, setFilterPrefix] = useState("");
  const [filterDatum, setFilterDatum] = useState("");
  const [filterText, setFilterText] = useState("");

  /* == Geodaten == */
  const [geodaten, setGeodaten] = useState(null);
  const [geodatenLoading, setGeodatenLoading] = useState(false);

  /* == OCR-Panel == */
  const [ocrOpen, setOcrOpen] = useState(false);

  /* == Geodaten Config == */
  const [geoConfigOpen, setGeoConfigOpen] = useState(false);
  const [geoConfigItems, setGeoConfigItems] = useState([]);
  const [geoConfigEdit, setGeoConfigEdit] = useState(null); // {id, lat, lon, radius_m, adresse, objekt, prefix, pfad, bemerkung}

  const selected = selectedIdx >= 0 && selectedIdx < dateien.length ? dateien[selectedIdx] : null;
  const selectedPath = selected?.path || null;

  /* == Katalog-Einheiten laden (für Prefix-Dropdown, aus zaehler_katalog) == */
  useEffect(() => {
    api("/bilder/katalog-einheiten")
      .then((data) => setKatalogEinheiten(data.einheiten || []))
      .catch(() => {});
  }, []);

  /* == Zähler-Arten laden (für Art-Dropdown, aus zaehler_config) == */
  useEffect(() => {
    api("/bilder/zaehler-arten")
      .then((data) => {
        if (data.arten && data.arten.length > 0) {
          // Merge: DB-Werte + ART_OPTIONS-Fallbacks, dedupliziert, sortiert
          const merged = [...new Set([...data.arten, ...ART_OPTIONS])].sort();
          setZaehlerArten(merged);
        }
      })
      .catch(() => {});
  }, []);

  /* == Geodaten Config laden == */
  const loadGeoConfig = useCallback(() => {
    api("/bilder/geodaten-config")
      .then((data) => setGeoConfigItems(data.items || []))
      .catch(() => {});
  }, []);
  useEffect(() => { loadGeoConfig(); }, [loadGeoConfig]);

  const saveGeoConfig = useCallback(async (item) => {
    if (item.id) {
      await fetch(API_BASE + `/zuordnen/api/bilder/geodaten-config/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(item),
      });
    } else {
      await apiPost("/bilder/geodaten-config", item);
    }
    loadGeoConfig();
  }, [loadGeoConfig]);

  const deleteGeoConfig = useCallback(async (id) => {
    await fetch(API_BASE + `/zuordnen/api/bilder/geodaten-config/${id}`, { method: "DELETE" });
    loadGeoConfig();
  }, [loadGeoConfig]);

  /* == Bilder laden (nur Dateiliste, ohne Analyse) == */
  const loadDateien = useCallback(async (folder) => {
    setPhase("loading");
    setLoadMsg("Zählerstände laden...");
    setAnalysed(false);
    const folderParam = folder || sourceFolder;

    try {
      let files;
      let ordnerResult;
      if (nurDateien && nurDateien.length > 0) {
        // Spezifische Dateien laden (aus Komprimierung-Seite)
        const resp = await api("/bilder/dateien-multi", {
          method: "POST",
          body: JSON.stringify({ dateien: nurDateien }),
        });
        files = resp.dateien || [];
        ordnerResult = resp.ordner || folderParam;
      } else {
        const qs = folderParam ? "?ordner=" + encodeURIComponent(folderParam) : "";
        const data = await api("/bilder/dateien" + qs);
        files = data.dateien || [];
        ordnerResult = data.ordner || "";
      }

      setDateien(files);
      setInbox(ordnerResult);
      const settings = {};
      for (const d of files) {
        settings[d.path] = {
          prefix: "",
          art: "",
          stand: "",
          einheit: "",
          zaehlernummer: "",
          foto_datum: "",
          ziel_ordner: modus === "umbenennen" ? d.path.substring(0, d.path.lastIndexOf("/")) : "",
          confidence: "none",
          reason: "",
          kopie_nok: false,
          dateiname_neu: "",
          obis_code: "1.8.0",
          excel_eintrag: false,
        };
      }
      setFileSettings(settings);
      setSelectedIdx(files.length > 0 ? 0 : -1);
      setPhase("ready");
    } catch (e) {
      setError(e.message);
      setPhase("error");
    }
  }, [sourceFolder, nurDateien]);

  useEffect(() => { loadDateien(); }, [loadDateien]);

  /* == Analyse (Barcode + OCR + Matching - volle Pipeline) == */
  /* Welle 78: Zählernummer aus dem Katalog, wenn Präfix bekannt, Nummer aber leer
     (z.B. Smart-Meter-App-Screenshot "101 Strom"): Kandidaten nach Art filtern —
     genau einer → direkt eintragen, mehrere → Auswahl am Feld anbieten. */
  const KATALOG_ART_MAP = { Strom: "Strom", Gas: "Gas", Wasser: "Wasser", Kaltwasser: "Wasser", Warmwasser: "Wasser", Heizung: "Heizung", "Zwischenzähler": "Zwischenzähler", "Öl": "Öl", Allgemein: "Allgemein", Wärme: "Heizung" };
  const katalogKandidaten = useCallback((liste, art) => {
    const alle = Array.isArray(liste) ? liste : [];
    if (!art) return alle;
    // Welle 80: NUR die gewählte Art (Ingvar: „hier steht Wasser, er zeigt alle") — kein Fallback auf alle
    return alle.filter((z) => (KATALOG_ART_MAP[z.zaehler_art] || z.zaehler_art) === art);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const katalogAutoFill = useCallback((path) => {
    setFileSettings((prev) => {
      const cur = prev[path];
      if (!cur || cur.zaehlernummer || !cur.prefix) return prev;
      const kand = katalogKandidaten(cur._katalog_zaehler, cur.art);
      if (kand.length !== 1) return prev;
      const z = kand[0];
      return {
        ...prev,
        [path]: {
          ...cur,
          zaehlernummer: z.zaehlernummer || "",
          art: cur.art || KATALOG_ART_MAP[z.zaehler_art] || "",
          obis_code: cur.obis_code || z.obis_register || "1.8.0",
          stand: (!cur.stand || cur.stand === "") && z.stand != null ? ganz(z.stand) : cur.stand || "",
          reason: (cur.reason || "") + ` | Zähler ${z.zaehlernummer} aus Katalog (${z.zaehler_art})`,
          _znr_aus_katalog: true,
        },
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [katalogKandidaten]);

  const runAnalyse = useCallback(async () => {
    setPhase("analysing");
    setLoadMsg("Zählerstände werden analysiert (Barcode + OCR + Matching)...");
    const qs = sourceFolder ? "?ordner=" + encodeURIComponent(sourceFolder) : "";
    try {
      const data = await fetch(API_BASE + "/zuordnen/api/bilder/analyse" + qs, { method: "POST" })
        .then(async (r) => {
          if (!r.ok) {
            let detail = "";
            try { const j = await r.json(); detail = j.detail || ""; } catch {}
            throw new Error("API " + r.status + (detail ? ": " + detail : ""));
          }
          return r.json();
        });

      // Wenn Analyse noch läuft → nicht überschreiben
      if (data.status === "running") {
        setError("Analyse läuft bereits – bitte kurz warten und erneut versuchen.");
        setPhase("ready");
        return;
      }

      const analysedFiles = data.dateien || [];

      // Dropbox-Dateiliste NEU laden (Dateien können durch OCR-Server umbenannt worden sein, z.B. .heic → .jpg)
      const folderParam = sourceFolder ? "?ordner=" + encodeURIComponent(sourceFolder) : "";
      const freshData = await api("/bilder/dateien" + folderParam);
      const freshFiles = freshData.dateien || [];
      setDateien(freshFiles);

      // Analyse-Ergebnisse (Cache) mit tatsächlichen Dropbox-Dateien mergen.
      // Cache kann veraltete Einträge enthalten → NUR Dateien behalten die auf Dropbox existieren.
      const analyseByPath = {};
      for (const d of analysedFiles) analyseByPath[d.path] = d;

      // Dropbox-Dateien als Basis (frisch geladen), Analyse-Daten dazu mergen
      const currentDateien = freshFiles;
      const merged = currentDateien.map((d) => analyseByPath[d.path] ? { ...d, ...analyseByPath[d.path] } : d);

      setDateien(merged);

      // Settings aus Analyse übernehmen
      const settings = {};
      for (const d of merged) {
        const a = analyseByPath[d.path];
        settings[d.path] = {
          prefix: (a?.prefix) || "",
          art: (a?.art) || "",
          // Welle 77: Stand aus Analyse, sonst OCR-/KI-Cache-Wert (nie verstecken)
          stand: (a?.stand != null && a?.stand !== "") ? ganz(a.stand)
                 : (a?.ocr_stand != null && a?.ocr_stand !== "") ? ganz(a.ocr_stand) : "",
          _stand_aus_cache: !(a?.stand != null && a?.stand !== "") && (a?.ocr_stand != null && a?.ocr_stand !== ""),
          _letzter_stand: a?.letzter_stand ?? null,
          _letzter_stand_datum: a?.letzter_stand_datum || null,
          _quellen_ok: a?.quellen_ok || "",
          einheit: (a?.einheit) || "",
          zaehlernummer: (a?.zaehlernummer) || "",
          foto_datum: (a?.foto_datum) || "",
          ziel_ordner: modus === "umbenennen" ? (d.path ? d.path.substring(0, d.path.lastIndexOf("/")) : sourceFolder) : ((a?.ziel_ordner) || ""),
          confidence: (a?.confidence) || "red",
          reason: (a?.reason) || "",
          kopie_nok: false,
          dateiname_neu: "",
          excel_eintrag: false,
          match_methode: (a?.match_methode) || "",
          obis_code: (a?.obis_code) || "1.8.0",
          dateiname_vorschau: (a?.dateiname_vorschau) || "",
          // Welle 65 (SIJ-672): Gemini-Erkennung
          quelle: (a?.quelle) || "",
          beschriftung: (a?.beschriftung) || "",
          plausibilitaet: (a?.plausibilitaet) || "",
          _katalog_zaehler: (a?.katalog_zaehler) || [],
          _geodaten: (a?.geodaten) || null,
          _ocr_text_barcode: (a?.ocr_text_barcode) || "",
          _ocr_text_easy: (a?.ocr_text_easy) || "",
          _ocr_text_paddle: (a?.ocr_text_paddle) || "",
          cache_status: a ? (a.cache_status || "neu") : null,
        };
      }
      setFileSettings(settings);
      setSelectedIdx(merged.length > 0 ? _firstVisualIdx(merged, settings) : -1);
      setAnalysed(true);
      setPhase("ready");
      // Gruppen einfrieren: ab hier springt keine Karte mehr beim Bearbeiten
      const locked = {};
      for (const d of merged) locked[d.path] = _effectiveConf(settings[d.path]);
      setLockedGroups(locked);

      // Katalog-Stand nachladen für alle analysierten Dateien
      for (const d of merged) {
        const s = settings[d.path];
        if (s?.zaehlernummer) {
          // Zählernummer bekannt → direkter Stand-Check
          const znr = s.zaehlernummer;
          const datum = s.foto_datum;
          api("/bilder/katalog-check-stand?zaehlernummer=" + encodeURIComponent(znr) +
            (datum ? "&foto_datum=" + encodeURIComponent(datum) : ""))
            .then((ck) => {
              if (ck.found) {
                setFileSettings((prev) => {
                  const cur = prev[d.path] || {};
                  const updates = {};
                  if (ck.zaehlernummer_db) updates.zaehlernummer = ck.zaehlernummer_db;
                  if (ck.stand != null && (!cur.stand || cur.stand === "")) updates.stand = ganz(ck.stand);
                  if (ck.stand != null && cur._letzter_stand == null) { updates._letzter_stand = Math.floor(Number(ck.stand)); updates._letzter_stand_datum = ck.stand_datum || null; }
                  if (Object.keys(updates).length === 0) return prev;
                  return { ...prev, [d.path]: { ...cur, ...updates } };
                });
              }
            })
            .catch(() => {});
        } else if (s?.prefix && s._katalog_zaehler?.length >= 1) {
          // Welle 78: Katalog nach Art filtern — genau 1 → Auto-Fill, sonst Auswahl am Feld
          katalogAutoFill(d.path);
        } else if (s?.prefix && !s._katalog_zaehler?.length) {
          // Keine Katalog-Daten vom Backend → per API nachladen
          api("/bilder/katalog-by-prefix?prefix=" + encodeURIComponent(s.prefix))
            .then((res) => {
              if (res.zaehler && res.zaehler.length >= 1) {
                setFileSettings((prev) => ({
                  ...prev,
                  [d.path]: { ...(prev[d.path] || {}), _katalog_zaehler: res.zaehler },
                }));
                katalogAutoFill(d.path);
              }
            })
            .catch(() => {});
        }
      }
    } catch (e) {
      setError(e.message);
      setPhase("error");
    }
  }, [katalogAutoFill, sourceFolder]);

  /* == Reset Rotation/Zoom bei Bildwechsel == */
  useEffect(() => {
    setImgRotation(0);
    setImgZoom(1);
    setImgPan({ x: 0, y: 0 });
  }, [selectedIdx]);

  /* == Geodaten aus Analyse-Cache (kein separater API-Call mehr) == */
  useEffect(() => {
    const path = (selectedIdx >= 0 && selectedIdx < dateien.length) ? dateien[selectedIdx]?.path : null;
    if (!path) { setGeodaten(null); return; }
    const s = fileSettings[path];
    const geo = s?._geodaten;
    if (geo && (geo.lat || geo.lon)) {
      setGeodaten({ status: "ok", latitude: geo.lat, longitude: geo.lon, altitude: geo.alt, adresse: geo.adresse });
    } else {
      setGeodaten(null);
    }
    setGeodatenLoading(false);
  }, [selectedIdx, dateien, fileSettings]);

  /* == Thumbnail == */
  useEffect(() => {
    const path = (selectedIdx >= 0 && selectedIdx < dateien.length) ? dateien[selectedIdx]?.path : null;
    if (!path) { setThumbnail(null); return; }
    if (thumbCache.current[path]) { setThumbnail(thumbCache.current[path]); return; }
    setThumbLoading(true);
    setThumbnail(null);
    api("/bilder/thumbnail?path=" + encodeURIComponent(path) + "&max_size=800")
      .then((data) => {
        thumbCache.current[path] = { image: data.image, type: data.content_type };
        setThumbnail({ image: data.image, type: data.content_type });
      })
      .catch(() => setThumbnail(null))
      .finally(() => setThumbLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdx, dateien, thumbRefresh]);

  /* == Keyboard — navigiert in visueller Gruppenreihenfolge == */
  useEffect(() => {
    const handler = (e) => {
      if (phase !== "ready" || dateien.length === 0) return;
      if (browseOpen) return;
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
      // Build visual order: group → items in group (same order as render, nur gefilterte)
      const visualOrder = [];
      const grouped = {};
      // filteredDateien lokal berechnen (useMemo ist weiter unten definiert)
      const localFiltered = dateien.filter(d => {
        const s = fileSettings[d.path] || {};
        if (filterArt === "__none__" && s.art) return false;
        if (filterArt && filterArt !== "__none__" && s.art !== filterArt) return false;
        if (filterPrefix) {
          const q = filterPrefix.toLowerCase();
          if (!(s.prefix || "").toLowerCase().includes(q) && !(s.zaehlernummer || "").toLowerCase().includes(q)) return false;
        }
        if (filterDatum && !(s.foto_datum || "").includes(filterDatum)) return false;
        if (filterText) {
          const terms = filterText.toLowerCase().split(/\s+/).filter(Boolean);
          const haystack = [d.name, s.zaehlernummer || "", s.prefix || "", s.reason || "", s.ocr_text || "", s.ziel_ordner || ""].join(" ").toLowerCase();
          if (!terms.every(t => haystack.includes(t))) return false;
        }
        return true;
      });
      localFiltered.forEach((d) => {
        const origIdx = dateien.indexOf(d);
        const conf = (fileSettings[d.path] || {}).confidence || "none";
        if (!grouped[conf]) grouped[conf] = [];
        grouped[conf].push(origIdx);
      });
      for (const g of GROUP_ORDER) {
        if (grouped[g.key]) visualOrder.push(...grouped[g.key]);
      }
      if (e.key === "ArrowDown" || e.key === "j" || e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        const curPos = visualOrder.indexOf(selectedIdx);
        if (e.key === "ArrowDown" || e.key === "j") {
          const nextPos = Math.min(curPos + 1, visualOrder.length - 1);
          setSelectedIdx(visualOrder[nextPos]);
        } else {
          const prevPos = Math.max(curPos - 1, 0);
          setSelectedIdx(visualOrder[prevPos]);
        }
        setMoveResult(null);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const sel = dateien[selectedIdx];
        if (sel) {
          const wasChecked = checkedPaths.has(sel.path);
          setCheckedPaths((prev) => {
            const n = new Set(prev);
            if (n.has(sel.path)) n.delete(sel.path); else n.add(sel.path);
            return n;
          });
          // Zur nächsten Datei springen
          const curPos = visualOrder.indexOf(selectedIdx);
          const nextPos = Math.min(curPos + 1, visualOrder.length - 1);
          if (visualOrder[nextPos] !== undefined && visualOrder[nextPos] !== selectedIdx) setSelectedIdx(visualOrder[nextPos]);
          // Async Duplikat-Check wenn gerade angecheckt + Zielordner vorhanden
          if (!wasChecked) {
            const s = fileSettings[sel.path];
            if (s?.ziel_ordner) {
              apiPost("/bilder/duplikat-check", {
                quell_pfad: sel.path, ziel_ordner: s.ziel_ordner, dateiname_neu: "", obis_code: s.obis_code || "",
              }).then((res) => {
                if (res?.duplikat) {
                  setDuplikatWarnungen((prev) => ({ ...prev, [sel.path]: { existierend: res.existierende_datei, duplikat_typ: res.duplikat_typ, quell: res.quell_datei } }));
                } else {
                  setDuplikatWarnungen((prev) => { const n = { ...prev }; delete n[sel.path]; return n; });
                }
              }).catch(() => {});
            }
          } else {
            // Unchecked → Warnung entfernen
            setDuplikatWarnungen((prev) => { const n = { ...prev }; delete n[sel.path]; return n; });
          }
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [phase, dateien, fileSettings, browseOpen, selectedIdx, filterArt, filterPrefix, filterDatum, filterText, checkedPaths]);

  useEffect(() => {
    if (selectedIdx >= 0 && cardRefs.current[selectedIdx]) {
      cardRefs.current[selectedIdx].scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedIdx, scrollTick]);

  /* == Zähler-Such-Dropdown bei Kartenwechsel / Escape schließen == */
  useEffect(() => {
    setZaehlerSuchResults([]);
    setZaehlerSuchPath(null);
  }, [selectedIdx]);

  /* == Keyboard-Nav: Fokus auf Stand-Input bei Kartenwechsel == */
  useEffect(() => {
    const path = (selectedIdx >= 0 && selectedIdx < dateien.length) ? dateien[selectedIdx]?.path : null;
    if (path) setTimeout(() => standInputRefs.current[path]?.focus(), 60);
  }, [selectedIdx, dateien]);

  /* == Keyboard-Nav: Fokus auf "Ersetzen"-Button wenn Duplikat-Modal öffnet == */
  useEffect(() => {
    if (duplikatInfo) setTimeout(() => duplikatReplaceRef.current?.focus(), 60);
  }, [duplikatInfo]);

  /* == Duplikat-Modal: Thumbnail für Quell-Datei laden == */
  useEffect(() => {
    if (!duplikatInfo) { setDuplikatQuellThumb(null); return; }
    const url = duplikatInfo.quell?.vorschau_url;
    if (url) { setDuplikatQuellThumb({ src: url }); return; }
    const path = duplikatInfo.quell?.path;
    if (path) {
      api("/bilder/thumbnail?path=" + encodeURIComponent(path) + "&max_size=400")
        .then((d) => setDuplikatQuellThumb({ src: "data:" + (d.content_type || "image/jpeg") + ";base64," + d.image }))
        .catch(() => setDuplikatQuellThumb(null));
    }
  }, [duplikatInfo]);

  /* == Trello-Modal: Fokus auf 'Card beim Verschieben'-Button == */
  useEffect(() => {
    if (trelloPopupOpen) setTimeout(() => trelloBeimVerschiebenRef.current?.focus(), 60);
  }, [trelloPopupOpen]);

  /* == Helpers == */
  const shortPath = (p) => {
    if (!p) return "";
    const idx = p.indexOf("Holthey Immobilien/");
    return idx >= 0 ? p.substring(idx + 19) : p;
  };

  /* == Ziel automatisch setzen wenn Prefix geändert wird == */
  const updatePrefix = useCallback(async (path, prefix) => {
    setFileSettings((prev) => {
      const s = prev[path] || {};
      const confidence = prefix ? "green" : "red";
      const reason = prefix ? `Prefix: ${prefix}` : "Kein Prefix";
      // Beim Prefix-Wechsel: Zähler-abhängige Felder zurücksetzen
      return { ...prev, [path]: { ...s, prefix, confidence, reason, zaehlernummer: "", art: "", stand: "", _katalog_zaehler: undefined } };
    });

    // Katalog-Abfrage: Zähler für diesen Prefix laden
    if (prefix) {
      try {
        const res = await api("/bilder/katalog-by-prefix?prefix=" + encodeURIComponent(prefix));
        if (res.zaehler && res.zaehler.length > 0) {
          setFileSettings((prev) => {
            const s = prev[path] || {};
            const artMap = { "Strom": "Strom", "Gas": "Gas", "Wasser": "Wasser", "Kaltwasser": "Wasser", "Warmwasser": "Wasser", "Heizung": "Heizung" };
            if (res.zaehler.length === 1) {
              const z = res.zaehler[0];
              return {
                ...prev,
                [path]: {
                  ...s,
                  zaehlernummer: z.zaehlernummer || "",
                  art: artMap[z.zaehler_art] || "",
                  stand: "",  // kein Stand – nur bei exaktem Datum-Match (katalog-check-stand)
                  _katalog_zaehler: res.zaehler,
                  reason: `Prefix: ${prefix} → ${z.zaehlernummer} (${z.zaehler_art})`,
                },
              };
            }
            // Mehrere Zähler → nur speichern, User wählt im Dropdown
            return { ...prev, [path]: { ...s, _katalog_zaehler: res.zaehler } };
          });

          // Wenn nur ein Zähler: katalog-check-stand mit foto_datum für exakten Stand
          if (res.zaehler.length === 1) {
            const z = res.zaehler[0];
            const cur = { ...((fileSettings[path]) || {}), zaehlernummer: z.zaehlernummer };
            const datum = cur.foto_datum;
            if (z.zaehlernummer && datum) {
              try {
                const ck = await api("/bilder/katalog-check-stand?zaehlernummer=" + encodeURIComponent(z.zaehlernummer) + "&foto_datum=" + encodeURIComponent(datum));
                if (ck.found) {
                  setFileSettings((prev) => {
                    const s = prev[path] || {};
                    return {
                      ...prev,
                      [path]: {
                        ...s,
                        zaehlernummer: ck.zaehlernummer_db || s.zaehlernummer,
                        stand: ck.stand != null ? ganz(ck.stand) : s.stand,
                        reason: (s.reason || "") + ` | DB-Stand: ${ck.stand} (${ck.stand_datum || "?"})`,
                      },
                    };
                  });
                }
              } catch (_) {}
            }
          }
        }
      } catch (e) {
        console.warn("Katalog-Abfrage fehlgeschlagen:", e);
      }
    }
  }, [fileSettings]);

  const updateSetting = useCallback((path, key, value) => {
    setFileSettings((prev) => ({
      ...prev,
      [path]: { ...prev[path], [key]: value },
    }));
  }, []);

  /* == Auto-Save Stand/Zählernummer to ocr-cache == */
  const saveToCache = useCallback((path) => {
    if (!path || savingRef.current) return;
    // Read current fileSettings to get the data
    setFileSettings((prev) => {
      const s = prev[path];
      if (!s) return prev;
      const fotoName = path.split("/").pop();
      // Only save if there's meaningful data
      if (!s.stand && !s.zaehlernummer) return prev;
      savingRef.current = true;
      fetch(API_BASE + "/zaehlerstand/ocr-cache-by-foto", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          foto_name: fotoName,
          stand: s.stand || null,
          zaehlernummer: s.zaehlernummer || null,
          einheit: s.einheit || null,
          art: s.art || null,
          // Welle 77: nur echte Cache-Status senden — Confidence ("green"/"") gehört nicht in status
          status: ["neu", "bearbeitung", "bestaetigt", "verschoben", "fehler"].includes(s.cache_status) ? s.cache_status : undefined,
          obis_code: s.obis_code || null,
        }),
      })
        .then(() => { /* saved */ })
        .catch((e) => console.warn("Cache-Save fehlgeschlagen:", e))
        .finally(() => { savingRef.current = false; });
      return prev; // no state change
    });
  }, []);

  /* == Auto-Save on file change == */
  useEffect(() => {
    const prevIdx = prevSelectedIdx.current;
    prevSelectedIdx.current = selectedIdx;
    if (prevIdx >= 0 && prevIdx < dateien.length && prevIdx !== selectedIdx) {
      saveToCache(dateien[prevIdx]?.path);
    }
  }, [selectedIdx, dateien, saveToCache]);

  const toggleSetting = useCallback((path, key) => {
    setFileSettings((prev) => ({
      ...prev,
      [path]: { ...prev[path], [key]: !prev[path]?.[key] },
    }));
  }, []);

  const updateZiel = useCallback((path, ziel) => {
    setFileSettings((prev) => ({
      ...prev,
      [path]: { ...prev[path], ziel_ordner: ziel, confidence: "green", reason: "Manuell gewählt" },
    }));
  }, []);

  /* == Einzelne Datei neu analysieren == */
  const ocrPollingRef = useRef({});  // path → intervalId
  const analyseEinzel = useCallback(async (path) => {
    setAnalysingPath(path);
    try {
      const res = await api("/bilder/analyse-einzel?pfad=" + encodeURIComponent(path), { method: "POST" });
      if (res.status === "ocr_pending") {
        // OCR wurde getriggert → Reason setzen + Polling starten
        setFileSettings((prev) => ({
          ...prev,
          [path]: {
            ...prev[path],
            reason: "⏳ OCR läuft…",
            confidence: "red",
          },
        }));
        // Polling: alle 5s prüfen ob Cache jetzt da ist
        if (!ocrPollingRef.current[path]) {
          const intervalId = setInterval(async () => {
            try {
              const poll = await api("/bilder/analyse-einzel?pfad=" + encodeURIComponent(path), { method: "POST" });
              if (poll.status === "ok") {
                clearInterval(ocrPollingRef.current[path]);
                delete ocrPollingRef.current[path];
                // Prüfen ob Datei konvertiert wurde (neuer Pfad/Name)
                const newPath = poll.path || path;
                const newName = poll.name || path.split("/").pop();
                const keyPath = newPath !== path ? newPath : path;
                if (newPath !== path) {
                  // Datei wurde konvertiert (z.B. .jpeg → .jpg)
                  setDateien((prev) => prev.map(d => d.path === path ? { ...d, path: newPath, name: newName } : d));
                  setFileSettings((prev) => {
                    const next = { ...prev };
                    delete next[path];
                    return next;
                  });
                }
                setFileSettings((prev) => ({
                  ...prev,
                  [keyPath]: {
                    ...prev[keyPath],
                    prefix:        poll.prefix        || "",
                    art:           poll.art           || prev[keyPath]?.art || "",
                    zaehlernummer: poll.zaehlernummer || prev[keyPath]?.zaehlernummer || "",
                    stand:         poll.stand != null  ? ganz(poll.stand) : (poll.ocr_stand != null && poll.ocr_stand !== "" ? ganz(poll.ocr_stand) : (prev[keyPath]?.stand || "")),
                    _stand_aus_cache: poll.stand == null && poll.ocr_stand != null && poll.ocr_stand !== "",
                    _letzter_stand: poll.letzter_stand ?? null,
                    _letzter_stand_datum: poll.letzter_stand_datum || null,
                    _quellen_ok: poll.quellen_ok || "",
                    einheit:       poll.einheit       || prev[keyPath]?.einheit || "",
                    foto_datum:    poll.foto_datum    || prev[keyPath]?.foto_datum || "",
                    ziel_ordner:   modus === "umbenennen" ? keyPath.substring(0, keyPath.lastIndexOf("/")) : (poll.ziel_ordner || prev[keyPath]?.ziel_ordner || ""),
                    confidence:    poll.confidence    || "red",
                    reason:        poll.reason        || "",
                    match_methode: poll.match_methode || "",
                    obis_code:     poll.obis_code     || prev[keyPath]?.obis_code || "1.8.0",
                    dateiname_vorschau: poll.dateiname_vorschau || "",
                    _katalog_zaehler: poll.katalog_zaehler || prev[keyPath]?._katalog_zaehler || [],
                    _geodaten:     poll.geodaten      || prev[keyPath]?._geodaten || null,
                    _ocr_text_barcode: poll.ocr_text_barcode || "",
                    _ocr_text_easy:    poll.ocr_text_easy || "",
                    _ocr_text_paddle:  poll.ocr_text_paddle || "",
                  },
                }));
                if (!poll.zaehlernummer && poll.prefix) katalogAutoFill(keyPath);  // Welle 78
              }
              // still pending → keep polling
            } catch (_) { /* polling error → ignore, retry next interval */ }
          }, 5000);
          ocrPollingRef.current[path] = intervalId;
        }
        setAnalysingPath(null);
        return;
      }
      if (res.status === "ok") {
        // Prüfen ob Datei konvertiert wurde (neuer Pfad/Name vom Backend)
        const newPath = res.path || path;
        const newName = res.name || path.split("/").pop();
        const keyPath = newPath !== path ? newPath : path;
        if (newPath !== path) {
          // Datei wurde konvertiert (z.B. .jpeg → .jpg) → dateien + fileSettings aktualisieren
          setDateien((prev) => prev.map(d => d.path === path ? { ...d, path: newPath, name: newName } : d));
          setFileSettings((prev) => {
            const next = { ...prev };
            delete next[path];
            return next;
          });
        }
        setFileSettings((prev) => ({
          ...prev,
          [keyPath]: {
            ...prev[keyPath],
            prefix:        res.prefix        || "",
            art:           res.art           || prev[keyPath]?.art || "",
            zaehlernummer: res.zaehlernummer || prev[keyPath]?.zaehlernummer || "",
            stand:         res.stand != null  ? ganz(res.stand) : (res.ocr_stand != null && res.ocr_stand !== "" ? ganz(res.ocr_stand) : (prev[keyPath]?.stand || "")),
            _stand_aus_cache: res.stand == null && res.ocr_stand != null && res.ocr_stand !== "",
            _letzter_stand: res.letzter_stand ?? prev[keyPath]?._letzter_stand ?? null,
            _letzter_stand_datum: res.letzter_stand_datum || prev[keyPath]?._letzter_stand_datum || null,
            _quellen_ok: res.quellen_ok || prev[keyPath]?._quellen_ok || "",
            einheit:       res.einheit       || prev[keyPath]?.einheit || "",
            foto_datum:    res.foto_datum    || prev[keyPath]?.foto_datum || "",
            ziel_ordner:   modus === "umbenennen" ? keyPath.substring(0, keyPath.lastIndexOf("/")) : (res.ziel_ordner || prev[keyPath]?.ziel_ordner || ""),
            confidence:    res.confidence    || "red",
            reason:        res.reason        || "",
            match_methode: res.match_methode || "",
            quelle:        res.quelle        || prev[keyPath]?.quelle || "",
            plausibilitaet: res.plausibilitaet || "",
            obis_code:     res.obis_code     || prev[keyPath]?.obis_code || "1.8.0",
            dateiname_vorschau: res.dateiname_vorschau || "",
            _katalog_zaehler: res.katalog_zaehler || prev[keyPath]?._katalog_zaehler || [],
            _geodaten:     res.geodaten      || prev[keyPath]?._geodaten || null,
            _ocr_text_barcode: res.ocr_text_barcode || prev[keyPath]?._ocr_text_barcode || "",
            _ocr_text_easy:    res.ocr_text_easy    || prev[keyPath]?._ocr_text_easy || "",
            _ocr_text_paddle:  res.ocr_text_paddle  || prev[keyPath]?._ocr_text_paddle || "",
          },
        }));
        if (!res.zaehlernummer && res.prefix) katalogAutoFill(keyPath);  // Welle 78

        // Katalog-Check: Stand aus DB laden wenn Zählernummer bekannt
        const znr = res.zaehlernummer;
        const datum = res.foto_datum;
        if (znr) {
          try {
            const qs = "/bilder/katalog-check-stand?zaehlernummer=" + encodeURIComponent(znr) +
              (datum ? "&foto_datum=" + encodeURIComponent(datum) : "");
            const ck = await api(qs);
            if (ck.found) {
              setFileSettings((prev) => {
                const cur = prev[keyPath] || {};
                const updates = {};
                if (ck.zaehlernummer_db) updates.zaehlernummer = ck.zaehlernummer_db;
                if (ck.stand != null && (!cur.stand || cur.stand === "")) updates.stand = ganz(ck.stand);
                if (ck.stand != null && cur._letzter_stand == null) { updates._letzter_stand = Math.floor(Number(ck.stand)); updates._letzter_stand_datum = ck.stand_datum || null; }
                if (Object.keys(updates).length === 0) return prev;
                return { ...prev, [keyPath]: { ...cur, ...updates } };
              });
            }
          } catch (_) { /* Katalog-Check optional */ }
        }
      }
    } catch (e) {
      if (!e.message?.includes("404")) {
        alert("Analyse fehlgeschlagen: " + e.message);
      }
    } finally {
      setAnalysingPath(null);
    }
  }, [katalogAutoFill]);

  // Cleanup OCR-Polling bei Unmount
  useEffect(() => {
    return () => {
      Object.values(ocrPollingRef.current).forEach(clearInterval);
      ocrPollingRef.current = {};
    };
  }, []);

  /* == Auto-Analyse beim Navigieren von HomePage == */
  useEffect(() => {
    if (phase === "ready" && !analysed && location.state?.autoAnalyse && !autoAnalyseDone.current) {
      autoAnalyseDone.current = true;
      if (nurDateien && nurDateien.length > 0) {
        // nurDateien-Modus: jede Datei einzeln analysieren (Cache-basiert)
        // 404 = kein Cache → überspringen (Datei bleibt rot/unanalysiert)
        (async () => {
          setPhase("analysing");
          setLoadMsg(`Analyse läuft (${dateien.length} Dateien)...`);
          for (const d of dateien) {
            try {
              await analyseEinzel(d.path);
            } catch (_) {
              // Kein Cache → ignorieren, Datei bleibt unanalysiert
            }
          }
          setAnalysed(true);
          setPhase("ready");
        })();
      } else {
        runAnalyse();
      }
    }
  }, [phase, analysed, location.state, runAnalyse, nurDateien, dateien, analyseEinzel]);

  /* == Zählernummer abgleichen == */
  const [matchingPath, setMatchingPath] = useState(null);
  const [analysingPath, setAnalysingPath] = useState(null);
  const [rescanningPath, setRescanningPath] = useState(null);
  const [enginePickerFile, setEnginePickerFile] = useState(null); // SIJ-672: {path,name} oder {alle:true}

  /* == Welle 65 (SIJ-672): Gemini-Status + Toggle == */
  const [geminiStatus, setGeminiStatus] = useState(null); // {key_vorhanden, enabled}
  useEffect(() => {
    fetch(API_BASE + "/zaehlerstand/gemini-status")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setGeminiStatus(d))
      .catch(() => {});
  }, []);
  const toggleGemini = useCallback(async () => {
    if (!geminiStatus) return;
    const neu = !geminiStatus.enabled;
    try {
      await apiPostDirect("/zaehlerstand/gemini-toggle?enabled=" + neu, {});
      setGeminiStatus((g) => ({ ...g, enabled: neu }));
    } catch (e) { alert("Umschalten fehlgeschlagen: " + e.message); }
  }, [geminiStatus]);
  const [ocrStatus, setOcrStatus] = useState(null);  // Livefeed vom OCR-Server

  /* == Zähler aus Suchergebnis auswählen → match-zaehler aufrufen == */
  const selectZaehlerResult = useCallback(async (path, result) => {
    setZaehlerSuchResults([]);
    setZaehlerSuchPath(null);
    setMatchingPath(path);
    try {
      const res = await apiPost("/bilder/match-zaehler", { zaehlernummer: result.zaehlernummer_db, bild_pfad: path });
      if (res.ok) {
        const artMap = { "Kaltwasser": "Wasser", "Warmwasser": "Wasser", "Strom": "Strom", "Gas": "Gas", "Wasser": "Wasser", "Heizung": "Heizung", "Öl": "Öl", "Zwischenzähler": "Zwischenzähler", "Allgemein": "Allgemein" };
        const mappedArt = artMap[res.art] || res.art;
        setFileSettings((prev) => ({
          ...prev,
          [path]: {
            ...prev[path],
            prefix:      res.prefix      || prev[path].prefix,
            art:         mappedArt       || prev[path].art,
            ziel_ordner: modus === "umbenennen" ? prev[path].ziel_ordner : (res.ziel_ordner || prev[path].ziel_ordner),
            confidence:  res.confidence  || prev[path].confidence,
            reason:      res.reason      || prev[path].reason,
            match_methode: res.match_methode || prev[path].match_methode,
            foto_datum:  res.foto_datum  || prev[path].foto_datum,
            zaehlernummer: res.zaehlernummer_db || prev[path].zaehlernummer,
            stand: (!prev[path].stand && res.katalog_stand != null) ? ganz(res.katalog_stand) : prev[path].stand,
          },
        }));
        if (res.katalog_stand != null) {
          setMoveResult({ ok: true, msg: `Letzter Stand: ${res.katalog_stand} (${res.katalog_stand_datum || "?"})` });
        }
        // Automatisch neue Analyse triggern
        analyseEinzel(path);
      }
    } catch (e) {
      alert("Abgleich fehlgeschlagen: " + e.message);
    } finally {
      setMatchingPath(null);
    }
  }, [analyseEinzel]);

  const matchZaehler = useCallback(async (path) => {
    const s = fileSettings[path];
    const znr = s?.zaehlernummer?.replace(/\s/g, "");
    if (!znr) return;
    setMatchingPath(path);
    try {
      const res = await fetch(API_BASE + "/zuordnen/api/bilder/katalog-suche?q=" + encodeURIComponent(znr));
      const data = await res.json();
      const results = data.results || [];
      if (results.length === 1 && results[0].score === 100) {
        // Exakter Treffer → direkt zuordnen + neue Analyse
        await selectZaehlerResult(path, results[0]);
      } else if (results.length > 0) {
        // Dropdown mit Ergebnissen anzeigen
        setZaehlerSuchResults(results);
        setZaehlerSuchPath(path);
      } else {
        // Kein Treffer → direkt Anlegen anbieten
        setZaehlerSuchResults([]);
        setZaehlerSuchPath(null);
        setAnlegenModal({ path, zaehlernummer: znr });
      }
    } catch (e) {
      alert("Suche fehlgeschlagen: " + e.message);
    } finally {
      setMatchingPath(null);
    }
  }, [fileSettings, selectZaehlerResult]);

  /* == OCR-Server Livefeed Polling == */
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const r = await fetch(API_BASE + "/zuordnen/api/bilder/ocr-status");
        if (r.ok && active) setOcrStatus(await r.json());
      } catch { if (active) setOcrStatus(null); }
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => { active = false; clearInterval(iv); };
  }, []);

  /* == Einzelne Datei neu via OCR-Server analysieren == */
  /* == Welle 65 (SIJ-672): Gemini-KI-Erkennung == */
  const kiErkennenEinzeln = useCallback(async (path) => {
    setRescanningPath(path);
    setFileSettings((prev) => ({
      ...prev,
      [path]: { ...prev[path], cache_status: "bearbeitung" },
    }));
    try {
      const r = await apiPostDirect("/zaehlerstand/gemini-erkennen-einzeln", { foto_pfad: path });
      // Welle 77: KI-Antwort sofort in die Maske (Nummer/Stand/Art/OBIS),
      // analyseEinzel liefert danach Prefix/Ziel/Plausibilität aus dem Cache.
      if (r && r.status === "ok") {
        setFileSettings((prev) => ({
          ...prev,
          [path]: {
            ...prev[path],
            zaehlernummer: r.zaehlernummer || prev[path]?.zaehlernummer || "",
            stand: r.stand != null ? ganz(r.stand) : (prev[path]?.stand || ""),
            art: prev[path]?.art || r.art || "",
            einheit: r.einheit || prev[path]?.einheit || "",
            obis_code: r.obis_code || prev[path]?.obis_code || "1.8.0",
            quelle: "gemini",
            beschriftung: r.beschriftung || prev[path]?.beschriftung || "",
            cache_status: "neu",
          },
        }));
      }
      await analyseEinzel(path);
    } catch (e) {
      setFileSettings((prev) => ({
        ...prev,
        [path]: { ...prev[path], cache_status: "fehler" },
      }));
      alert("KI-Erkennung fehlgeschlagen: " + e.message);
    } finally {
      setRescanningPath(null);
    }
  }, [analyseEinzel]);

  const rescanEinzeln = useCallback(async (path) => {
    setRescanningPath(path);
    // Status sofort auf "bearbeitung" setzen für sofortiges visuelles Feedback
    setFileSettings((prev) => ({
      ...prev,
      [path]: { ...prev[path], cache_status: "bearbeitung" },
    }));
    try {
      await apiPostDirect("/zaehlerstand/scan/force-rescan-einzeln", { foto_pfad: path });
      // Polling: warte bis OCR fertig (status != "bearbeitung"), max 300s (kürzeres Intervall)
      const fotoName = path.split("/").pop();
      let fertig = false;
      for (let i = 0; i < 100; i++) { // 100 * 3s = 300s max
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const res = await fetch(API_BASE + "/zaehlerstand/ocr-cache-by-foto?foto_name=" + encodeURIComponent(fotoName));
          if (res.ok) {
            const data = await res.json();
            if (data.status !== "bearbeitung") { fertig = true; break; }
          }
        } catch (_) { /* weiter pollen */ }
      }
      if (fertig) {
        analyseEinzel(path);
      } else {
        setFileSettings((prev) => ({
          ...prev,
          [path]: { ...prev[path], cache_status: "fehler" },
        }));
        alert("OCR-Analyse dauert zu lange. Bitte später erneut versuchen.");
      }
    } catch (e) {
      setFileSettings((prev) => ({
        ...prev,
        [path]: { ...prev[path], cache_status: "fehler" },
      }));
      alert("Rescan fehlgeschlagen: " + e.message);
    } finally {
      setRescanningPath(null);
    }
  }, [analyseEinzel]);

  /* == Welle 80: gemeinsamer Engine-Picker → Analyse-Service ==
     cache  = nur Zuordnung neu (analyseEinzel)
     alle   = Metadaten + OCR-Server + Gemini (Standard)
     ocr    = Metadaten + OCR-Server       gemini = Metadaten + Gemini */
  const ENGINE_QUELLEN = { alle: ["meta", "ocr", "gemini"], ocr: ["meta", "ocr"], gemini: ["meta", "gemini"] };
  const analyseMitEngine = useCallback(async (path, engine) => {
    setRescanningPath(path);
    setFileSettings((prev) => ({ ...prev, [path]: { ...prev[path], cache_status: "bearbeitung" } }));
    try {
      await api("/bilder/analyse-einzel?pfad=" + encodeURIComponent(path) + "&engine=" + engine, { method: "POST" });
      const fotoName = path.split("/").pop();
      let fertig = false;
      for (let i = 0; i < 120; i++) { // max 6 min
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const res = await fetch(API_BASE + "/zaehlerstand/ocr-cache-by-foto?foto_name=" + encodeURIComponent(fotoName));
          if (res.ok) {
            const data = await res.json();
            if (data.status !== "bearbeitung") { fertig = true; break; }
          }
        } catch (_) { /* weiter pollen */ }
      }
      if (fertig) {
        await analyseEinzel(path);
      } else {
        setFileSettings((prev) => ({ ...prev, [path]: { ...prev[path], cache_status: "fehler" } }));
        alert("Analyse dauert zu lange. Bitte später erneut versuchen.");
      }
    } catch (e) {
      setFileSettings((prev) => ({ ...prev, [path]: { ...prev[path], cache_status: "fehler" } }));
      alert("Analyse fehlgeschlagen: " + e.message);
    } finally {
      setRescanningPath(null);
    }
  }, [analyseEinzel]);

  const onAnalyseJobItem = useCallback((item) => {
    if (item && item.pfad) analyseEinzel(item.pfad);
  }, [analyseEinzel]);
  const { jobs: analyseJobs, registerJob: registerAnalyseJob, dismissJob: dismissAnalyseJob } = useVerschiebenJobs({
    pageKey: "zaehler_analyse",
    onItemResult: onAnalyseJobItem,
    onJobDone: (job) => setTimeout(() => dismissAnalyseJob(job.job_id), 4000),
  });
  const analyseJobLaeuft = Object.values(analyseJobs).some((j) => j.status === "running");
  const analyseJobFortschritt = Object.values(analyseJobs).filter((j) => j.status === "running")
    .reduce((acc, j) => ({ done: acc.done + (j.done || 0), total: acc.total + (j.total || 0), current: j.current_datei || acc.current }), { done: 0, total: 0, current: "" });

  const analyseAlle = useCallback(async (engine, pfade) => {
    try {
      const d = await apiPostDirect("/zaehlerstand/analyse-job", { pfade, quellen: ENGINE_QUELLEN[engine] || ENGINE_QUELLEN.alle, force: true });
      if (d?.job_id) {
        registerAnalyseJob(d.job_id, d.total || pfade.length);
        setFileSettings((prev) => {
          const n = { ...prev };
          for (const p of pfade) n[p] = { ...(n[p] || {}), cache_status: "bearbeitung" };
          return n;
        });
      }
    } catch (e) { alert("Analyse-Job konnte nicht gestartet werden: " + e.message); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerAnalyseJob]);

  const runEngine = useCallback(async (key, f) => {
    const pfade = f.alle
      ? (checkedPaths.size > 0 ? dateien.filter((d) => checkedPaths.has(d.path)).map((d) => d.path) : dateien.map((d) => d.path))
      : [f.path];
    if (pfade.length === 0) return;
    if (key === "cache") {
      if (f.alle && checkedPaths.size === 0 && !nurDateien) { runAnalyse(); return; }
      for (const p of pfade) { try { await analyseEinzel(p); } catch (_) { /* weiter */ } }
      return;
    }
    if (pfade.length === 1) analyseMitEngine(pfade[0], key); else analyseAlle(key, pfade);
  }, [checkedPaths, dateien, nurDateien, runAnalyse, analyseEinzel, analyseMitEngine, analyseAlle]);

  /* == Neuen Zähler im Katalog anlegen == */
  const handleKatalogAnlegen = useCallback(async (data) => {
    try {
      const res = await apiPost("/bilder/katalog-anlegen", data);
      if (res.ok) {
        setMoveResult({ ok: true, msg: `Zähler angelegt (ID: ${res.id})` });
        // Settings aktualisieren
        if (data.bild_pfad) {
          setFileSettings((prev) => ({
            ...prev,
            [data.bild_pfad]: {
              ...prev[data.bild_pfad],
              zaehlernummer: data.zaehlernummer,
              art: data.zaehler_art || prev[data.bild_pfad]?.art || "",
              confidence: "green",
              reason: "Neu angelegt im Katalog",
            },
          }));
        }
        // Katalog-Einheiten neu laden
        api("/bilder/katalog-einheiten").then((d) => setKatalogEinheiten(d.einheiten || [])).catch(() => {});
      }
    } catch (e) {
      alert("Anlegen fehlgeschlagen: " + e.message);
    }
    setAnlegenModal(null);
  }, []);

  /* == Rename == */
  const handleRename = useCallback(async (alterPfad, neuerName) => {
    const trimmed = neuerName.trim();
    if (!trimmed || trimmed === alterPfad.split("/").pop()) {
      setEditingPath(null);
      return;
    }
    try {
      const res = await apiPost("/bilder/umbenennen", { alter_pfad: alterPfad, neuer_name: trimmed });
      setDateien((prev) => prev.map((d) => d.path === alterPfad
        ? { ...d, name: res.neuer_name, path: res.neuer_pfad }
        : d
      ));
      setFileSettings((prev) => {
        const s = prev[alterPfad];
        if (!s) return prev;
        const next = { ...prev };
        delete next[alterPfad];
        next[res.neuer_pfad] = s;
        return next;
      });
    } catch (e) {
      alert("Umbenennen fehlgeschlagen: " + e.message);
    } finally {
      setEditingPath(null);
    }
  }, []);

  /* == Browse == */
  const browseFolder = useCallback(async (path) => {
    setBrowseLoading(true);
    try {
      const data = await api("/bilder/browse-ordner?path=" + encodeURIComponent(path));
      setBrowseItems(data.ordner || []);
      setBrowsePath(path);
    } catch (e) { console.warn(e); }
    finally { setBrowseLoading(false); }
  }, []);

  const openBrowse = useCallback((targetDatei) => {
    const datei = targetDatei || selected;
    if (!datei) return;
    const s = fileSettings[datei.path];
    const startPath = s?.ziel_ordner || "/Holthey Immobilien/Bilder";
    browseTargetPath.current = datei.path;
    setBrowseOpen(true);
    setBrowsePath(startPath);
    setNewFolderMode(false);
    setNewFolderName("");
    browseFolder(startPath);
  }, [selected, fileSettings, browseFolder]);

  const pickFolder = useCallback((folderPath) => {
    const path = browseTargetPath.current || selected?.path;
    if (path) updateZiel(path, folderPath);
    setBrowseOpen(false);
  }, [selected, updateZiel]);

  const createFolder = useCallback(async (parentPath, name, refreshFn) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const pfad = parentPath.replace(/\/$/, "") + "/" + trimmed;
    try {
      await apiPost("/bilder/ordner-erstellen", { pfad });
      await refreshFn(parentPath);
    } catch (e) {
      alert("Fehler beim Erstellen: " + e.message);
    }
  }, []);

  /* == Delete == */
  const handleDelete = useCallback(async (pfad) => {
    try {
      // Nächsten visuellen Pfad ermitteln (inline)
      const visualPaths = [];
      for (const g of GROUP_ORDER) {
        for (const d of dateien) {
          const conf = lockedGroups[d.path] || _effectiveConf(fileSettings[d.path]);
          if (conf === g.key) visualPaths.push(d.path);
        }
      }
      const vIdx = visualPaths.indexOf(pfad);
      const nextPath = visualPaths[vIdx + 1] ?? visualPaths[vIdx - 1] ?? null;

      await apiPost("/bilder/loeschen", { pfad });
      setDateien((prev) => {
        const next = prev.filter((d) => d.path !== pfad);
        const nextIdx = nextPath ? next.findIndex((d) => d.path === nextPath) : -1;
        setSelectedIdx(nextIdx >= 0 ? nextIdx : Math.max(0, next.length - 1));
        return next;
      });
      setLockedGroups((prev) => { const n = { ...prev }; delete n[pfad]; return n; });
      setScrollTick((t) => t + 1);
    } catch (e) {
      setMoveResult({ ok: false, msg: e.message });
    } finally {
      setConfirmDeletePath(null);
    }
  }, [dateien, lockedGroups, fileSettings]);

  /* == Duplizieren == */
  const handleDuplizieren = useCallback(async (pfad) => {
    setDuplicatingPath(pfad);
    try {
      const res = await apiPost("/bilder/duplizieren", { pfad });
      if (res.status === "ok" && res.neuer_pfad) {
        const neuerPfad = res.neuer_pfad;
        const neuerName = neuerPfad.split("/").pop();

        // Neue Datei in die Liste einfügen (direkt nach dem Original)
        let newIdx = -1;
        setDateien((prev) => {
          const idx = prev.findIndex((d) => d.path === pfad);
          const newFile = { name: neuerName, path: neuerPfad };
          const next = [...prev];
          next.splice(idx + 1, 0, newFile);
          newIdx = idx + 1;
          return next;
        });

        // FileSettings für die Kopie initialisieren
        setFileSettings((prev) => ({
          ...prev,
          [neuerPfad]: {
            prefix: "", art: "", stand: "", einheit: "",
            zaehlernummer: "", foto_datum: "", ziel_ordner: "",
            confidence: "none", reason: "Wird analysiert...",
            kopie_nok: false, nok_original: false, trello_enabled: false, dateiname_neu: "", obis_code: "1.8.0", excel_eintrag: false,
          },
        }));

        // Zur neuen Datei springen
        if (newIdx >= 0) {
          setSelectedIdx(newIdx);
          setScrollTick((t) => t + 1);
        }

        // Sofort Analyse starten
        setDuplicatingPath(null);
        analyseEinzel(neuerPfad);
        return;  // skip finally setDuplicatingPath
      }
    } catch (e) {
      alert("Duplizieren fehlgeschlagen: " + e.message);
    } finally {
      setDuplicatingPath(null);
    }
  }, [analyseEinzel]);

  /* == Dateiname-Helper für Move/Batch == */

  // Zählernummer: Slash/Minus/Whitespace entfernen, in 4er-Gruppen von rechts aufteilen
  const formatZnr4er = (znr) => {
    if (!znr) return "";
    const stripped = String(znr).replace(/[\s/\\-]/g, "");
    if (stripped.length <= 4) return stripped;
    const groups = [];
    let rest = stripped;
    while (rest.length > 4) {
      groups.unshift(rest.slice(-4));
      rest = rest.slice(0, -4);
    }
    if (rest) groups.unshift(rest);
    return groups.join(" ");
  };

  const _buildName = (filePath, s) => {
    if (s.dateiname_neu) return s.dateiname_neu;
    if (!s.prefix) return "";
    const ext = "." + filePath.split(".").pop().toLowerCase();
    // Prefix: "-Allg" entfernen → nur Objekt-ID (z.B. "010-Allg" → "010"), Einheit bleibt ("010-01")
    const prefixDisplay = s.prefix.replace(/-Allg$/i, "");
    // Welle 65.G (SIJ-672): Nachkommastellen NUR bei Ständen nahe 0
    // (0.27 → "0,27" — sonst stünde nur "0" im Namen); ab 1 ganzzahlig
    // (4002.5 → "4002").
    let standTxt = "";
    if (s.stand !== undefined && s.stand !== null && s.stand !== "") {
      const f = parseFloat(String(s.stand).replace(",", "."));
      if (!Number.isNaN(f)) standTxt = f < 1 ? String(f).replace(".", ",") : String(Math.floor(f));
    }
    // Slash/Backslash/Doppelpunkt sind in Dateinamen ungültig (Dropbox interpretiert / als Subordner!)
    const safe = (v) => String(v).replace(/[\\/:*?"<>|]/g, "");
    const parts = [safe(prefixDisplay)];
    if (s.foto_datum) parts.push(safe(s.foto_datum));
    if (s.zaehlernummer) parts.push(formatZnr4er(s.zaehlernummer));
    if (standTxt) parts.push(safe(standTxt));
    if (s.zaehlernummer && s.obis_code && s.art === "Strom") parts.push(safe(s.obis_code));
    return parts.length > 1 ? parts.join(" - ") + ext : "";
  };

  /* == Duplikat-Check Helper == */
  const checkDuplikat = useCallback(async (quellPfad, zielOrdner, dateinamNeu = "", obisCode = "") => {
    try {
      const res = await apiPost("/bilder/duplikat-check", {
        quell_pfad: quellPfad,
        ziel_ordner: zielOrdner,
        dateiname_neu: dateinamNeu,
        obis_code: obisCode,
      });
      if (res.duplikat) return res;
    } catch (e) { console.warn("Duplikat-Check fehlgeschlagen:", e); }
    return null;
  }, []);

  /* == Batch Duplikat-Check (für Auto + Batch) == */
  const checkBatchDuplikate = useCallback(async (items) => {
    // items: [{ quell_pfad, ziel_ordner, name }]
    const found = [];
    for (const item of items) {
      try {
        const res = await apiPost("/bilder/duplikat-check", { quell_pfad: item.quell_pfad, ziel_ordner: item.ziel_ordner });
        if (res.duplikat) {
          found.push({ quell: item, existierend: res.existierende_datei });
        }
      } catch (e) { /* ignore individual errors */ }
    }
    return found;
  }, []);

  /* == Trello == */
  const openTrelloPopup = useCallback((filePath) => {
    setTrelloPopupPath(filePath);
    setTrelloGrund("");
    const filename = filePath.split("/").pop() || "";
    setTrelloTitel(cleanFilenameForTrello(filename));
    setTrelloError(""); setTrelloSuccess("");
    setTrelloPopupOpen(true);
  }, []);

  const trelloCreateCard = useCallback(async (filePath, mode, grund, zielPfad, titel) => {
    setTrelloSaving(true); setTrelloError(""); setTrelloSuccess("");
    try {
      const resp = await fetch(API_BASE + "/zuordnen/api/trello/create-card", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quell_pfad: filePath, ziel_pfad: zielPfad || null, grund: grund || "", titel: titel || null }),
      });
      if (!resp.ok) {
        let d = ""; try { const j = await resp.json(); d = j.detail || ""; } catch {}
        throw new Error("API " + resp.status + (d ? ": " + d : ""));
      }
      const res = await resp.json();
      setTrelloSuccess("Card erstellt: " + res.card_url);
      setFileSettings(prev => ({
        ...prev,
        [filePath]: { ...prev[filePath], trello_enabled: true, trello_mode: mode, trello_grund: grund || "" },
      }));
      return res;
    } catch (e) { setTrelloError(e.message); return null; }
    finally { setTrelloSaving(false); }
  }, []);

  /* == Gefilterte Dateiliste (Prefix + Datum + Freitext + Art) == */
  const filteredDateien = useMemo(() => dateien.filter(d => {
    const s = fileSettings[d.path] || {};
    // Art-Filter
    if (filterArt === "__none__" && s.art) return false;
    if (filterArt && filterArt !== "__none__" && s.art !== filterArt) return false;
    // Prefix-Filter
    if (filterPrefix) {
      const q = filterPrefix.toLowerCase();
      const matchPrefix = (s.prefix || "").toLowerCase().includes(q);
      const matchZnr = (s.zaehlernummer || "").toLowerCase().includes(q);
      if (!matchPrefix && !matchZnr) return false;
    }
    // Datum-Filter
    if (filterDatum && !(s.foto_datum || "").includes(filterDatum)) return false;
    // Freitext Multi-Term
    if (filterText) {
      const terms = filterText.toLowerCase().split(/\s+/).filter(Boolean);
      const haystack = [d.name, s.zaehlernummer || "", s.prefix || "", s.reason || "", s.ocr_text || "", s.ziel_ordner || ""].join(" ").toLowerCase();
      if (!terms.every(t => haystack.includes(t))) return false;
    }
    return true;
  }), [dateien, fileSettings, filterArt, filterPrefix, filterDatum, filterText]);

  /* Hilfsfunktion: nächsten visuellen Pfad nach fromPath ermitteln */
  const _nextVisualPath = useCallback((fromPath) => {
    const visualPaths = [];
    for (const g of GROUP_ORDER) {
      for (const d of dateien) {
        const conf = lockedGroups[d.path] || _effectiveConf(fileSettings[d.path]);
        if (conf === g.key) visualPaths.push(d.path);
      }
    }
    const idx = visualPaths.indexOf(fromPath);
    return visualPaths[idx + 1] ?? visualPaths[idx - 1] ?? null;
  }, [dateien, lockedGroups, fileSettings]);

  /* == Hintergrund-Verschiebe-Jobs (Welle 28) ==
     Verschieben + Komprimieren + Trello laufen serverseitig als Job; das UI
     bleibt frei. Ergebnisse kommen per Poll: ok → Zeile raus, ORDNER_FEHLT →
     bekanntes Ordner-anlegen-Modal, sonst Fehler im Indikator pro Datei.
     (nutzt den bestehenden dateienRef von oben) */
  const selectedPathRef = useRef(null); // Fokus-Datei halten, wenn andere Zeilen verschwinden
  useEffect(() => { selectedPathRef.current = dateien[selectedIdx]?.path || null; }, [dateien, selectedIdx]);
  const onJobItemResult = useCallback((item) => {
    setInFlightPaths((prev) => { const n = new Set(prev); n.delete(item.quell_pfad); return n; });
    if (item.ok) {
      const nextPath = _nextVisualPath(item.quell_pfad);
      setDateien((prev) => {
        const selPath = selectedPathRef.current;
        const next = prev.filter((d) => d.path !== item.quell_pfad);
        // Fokus-Datei halten, wenn sie nicht selbst entfernt wurde
        if (selPath && selPath !== item.quell_pfad) {
          const ni = next.findIndex((d) => d.path === selPath);
          if (ni >= 0) { setSelectedIdx(ni); return next; }
        }
        const nextIdx = nextPath ? next.findIndex((d) => d.path === nextPath) : -1;
        setSelectedIdx(nextIdx >= 0 ? nextIdx : Math.max(0, next.length - 1));
        return next;
      });
      setLockedGroups((prev) => { const n = { ...prev }; delete n[item.quell_pfad]; return n; });
      setScrollTick((t) => t + 1);
    } else if (item.fehler && item.fehler.includes("ORDNER_FEHLT:")) {
      const missing = (item.fehler.split("ORDNER_FEHLT:")[1] || "").trim();
      const datei = dateienRef.current.find((d) => d.path === item.quell_pfad);
      if (datei) setOrdnerFehltModal({ pfad: missing, datei });
    }
  }, [_nextVisualPath]);

  const { jobs, startJob, dismissJob } = useVerschiebenJobs({
    pageKey: "bilder",
    onItemResult: onJobItemResult,
  });

  const buildJobItem = useCallback((datei) => {
    const s = fileSettings[datei.path] || {};
    const item = {
      quell_pfad: datei.path,
      ziel_ordner: s.ziel_ordner || "",
      dateiname_neu: _buildName(datei.path, s),
      prefix: s.prefix || "",
      art: s.art || "",
      stand: s.stand || null,
      einheit: s.einheit || "",
      zaehlernummer: (s.zaehlernummer || "").replace(/\s/g, ""),
      foto_datum: s.foto_datum || null,
      kopie_nok: s.kopie_nok || false,
      automatisch: false,
      skip_db: s.confidence === "manuell",
      obis_code: s.obis_code || null,
      excel_eintrag: s.excel_eintrag || false,
    };
    if (s.trello_enabled && s.trello_mode === "beim_verschieben") {
      // Prefix + Einheit mitgeben, damit der Kartentitel mit „005:" bzw.
      // „005-05:" beginnt (Welle 60).
      item.trello = {
        grund: s.trello_grund || "",
        titel: s.trello_titel || "",
        prefix: s.prefix || "",
        einheit: s.einheit || "",
      };
    }
    return item;
  }, [fileSettings, _buildName]);

  const submitJob = useCallback(async (items) => {
    if (!items.length) return;
    try {
      await startJob(items);
      setInFlightPaths((prev) => {
        const n = new Set(prev);
        items.forEach((it) => n.add(it.quell_pfad));
        return n;
      });
    } catch (e) {
      setInFlightPaths((prev) => {
        const n = new Set(prev);
        items.forEach((it) => n.delete(it.quell_pfad));
        return n;
      });
      setMoveResult({ ok: false, msg: `Verschieben konnte nicht gestartet werden: ${e.message}` });
    }
  }, [startJob]);

  /* == Move (als Hintergrund-Job) == */
  const _doMove = useCallback(async (datei) => {
    setMoveResult(null);
    await submitJob([buildJobItem(datei)]);
  }, [buildJobItem, submitJob]);

  /* == NOK Original: in NOK-Ordner — gleiche Job-Pipeline == */
  const moveNokOriginal = useCallback(async (datei) => {
    setMoveResult(null);
    await submitJob([{ quell_pfad: datei.path, ziel_ordner: "", nok_original: true }]);
  }, [submitJob]);

  /* == Bild → Fotos/Bilder-Ordner (kein Zählerstand-Eintrag) — Job-Pipeline,
        Ziel liegt unter /Bilder → wird dabei komprimiert == */
  const moveToBilder = useCallback(async (datei) => {
    setMoveResult(null);
    await submitJob([{
      quell_pfad: datei.path,
      ziel_ordner: BILDER_ZIEL,
      dateiname_neu: datei.name,
      skip_db: true,
    }]);
  }, [submitJob]);

  const handleMove = useCallback(async (targetDatei) => {
    const datei = targetDatei || selected;
    if (!datei) return;
    const s = fileSettings[datei.path];
    const effZiel = s?.ziel_ordner;
    if (!effZiel) { setMoveResult({ ok: false, msg: "Kein Ziel-Ordner" }); return; }

    // Neuen vorgeschlagenen Dateinamen für den Check verwenden
    const effName = _buildName(datei.path, s) || "";

    // Duplikat-Check VOR dem Verschieben
    setDuplikatChecking(true);
    const dup = await checkDuplikat(datei.path, effZiel, effName);
    setDuplikatChecking(false);

    if (dup) {
      // Duplikat gefunden → Popup zeigen
      const nextPathOnDel = _nextVisualPath(datei.path);
      const vorschauName = effName || datei.name || datei.path.split("/").pop();
      setDuplikatInfo({
        quell: { name: vorschauName, path: datei.path, size: dup.quell_datei?.size, vorschau_url: dup.quell_datei?.vorschau_url },
        existierend: dup.existierende_datei,
        duplikat_typ: dup.duplikat_typ,
        zielOrdner: effZiel,
        onDelete: async () => {
          // Quelldatei löschen (ist Duplikat)
          try {
            await apiPost("/bilder/loeschen", { pfad: datei.path });
            setDateien((prev) => {
              const next = prev.filter((d) => d.path !== datei.path);
              const nextIdx = nextPathOnDel ? next.findIndex((d) => d.path === nextPathOnDel) : -1;
              setSelectedIdx(nextIdx >= 0 ? nextIdx : Math.max(0, next.length - 1));
              return next;
            });
            setScrollTick((t) => t + 1);
            setMoveResult({ ok: true, msg: "Duplikat gelöscht" });
          } catch (e) {
            setMoveResult({ ok: false, msg: "Löschen fehlgeschlagen: " + e.message });
          }
          setDuplikatInfo(null);
        },
        onReplace: async () => {
          // Alte Datei im Ziel löschen, dann Quelldatei normal verschieben
          try {
            await apiPost("/bilder/loeschen", { pfad: dup.existierende_datei.path });
          } catch (e) {
            setMoveResult({ ok: false, msg: "Alte Datei löschen fehlgeschlagen: " + e.message });
            setDuplikatInfo(null);
            return;
          }
          setDuplikatInfo(null);
          await _doMove(datei);
        },
        onBeideBehalten: async () => {
          // Beide behalten: einfach verschieben — Dropbox benennt bei Konflikt auto um
          setDuplikatInfo(null);
          await _doMove(datei);
        },
        onAbort: () => setDuplikatInfo(null),
      });
      return;
    }

    // Kein Duplikat → normal verschieben
    await _doMove(datei);
  }, [selected, fileSettings, checkDuplikat, _doMove, _nextVisualPath, _buildName]);

  /* == Auto-Batch == */
  const autoReady = dateien.filter((d) => {
    if (inFlightPaths.has(d.path)) return false;
    const s = fileSettings[d.path];
    // Stand ist Pflichtfeld: kein Auto-Verschieben ohne Stand (wenn Zählernummer bekannt)
    const standOk = !!s.stand || !s.zaehlernummer;
    return s?.confidence === "auto" && !!s.ziel_ordner && standOk;
  });

  const handleAutoVerschieben = useCallback(async () => {
    setAutoMoving(true);
    // Sofort sichtbar: Zeilen ausgrauen + Status in der Ecke
    setInFlightPaths((prev) => { const n = new Set(prev); autoReady.forEach((d) => n.add(d.path)); return n; });
    setJobVorlauf("Duplikat-Prüfung läuft…");
    try {
      // 1) Duplikat-Check für alle Auto-Dateien
      const checkItems = autoReady.map((d) => {
        const s = fileSettings[d.path];
        return { quell_pfad: d.path, ziel_ordner: s.ziel_ordner, name: d.name };
      });
      const dups = await checkBatchDuplikate(checkItems);
      const dupPaths = new Set(dups.map((d) => d.quell?.quell_pfad));

      // Duplikate dem User zeigen (falls vorhanden) → bleiben aktiv
      if (dups.length > 0) setDuplikatList(dups);
      setInFlightPaths((prev) => { const n = new Set(prev); dupPaths.forEach((p) => n.delete(p)); return n; });

      // 2) Nur Nicht-Duplikate verschieben
      const nonDupReady = autoReady.filter((d) => !dupPaths.has(d.path));
      if (nonDupReady.length === 0) return;

      const batch = nonDupReady.map((d) => {
        const item = buildJobItem(d);
        item.automatisch = true;
        item.skip_db = false;
        return item;
      });
      await submitJob(batch);   // Hintergrund-Job: UI bleibt frei, Indikator zeigt Fortschritt
    } finally {
      setJobVorlauf("");
      setAutoMoving(false);
    }
  }, [autoReady, fileSettings, dateien, checkBatchDuplikate, buildJobItem, submitJob]);

  /* == Batch == */
  const batchReady = dateien.filter((d) => {
    if (inFlightPaths.has(d.path)) return false;
    const s = fileSettings[d.path];
    return (s?.confidence === "green" || s?.confidence === "manuell") && !!s.ziel_ordner;
  });
  // Wenn Checkboxen gesetzt: alle markierten Dateien mit ziel_ordner (egal welcher Status)
  // sonst: nur grüne/manuell (batchReady)
  const batchToProcess = checkedPaths.size > 0
    ? dateien.filter((d) => checkedPaths.has(d.path) && !!fileSettings[d.path]?.ziel_ordner)
    : batchReady;

  const handleBatch = useCallback(async () => {
    setShowBatch(false);
    setBatchMoving(true);
    setBatchResult(null);

    if (checkedPaths.size > 0) {
      // ── Markierte Dateien: EIN Hintergrund-Job für alle ──
      // (kein Duplikat-Check: Split-Batch-Dateien gehen in denselben Zielordner
      //  → würden fälschlich als Duplikat erkannt; Backend nutzt autorename.)
      const files = dateien.filter((d) => checkedPaths.has(d.path) && !!fileSettings[d.path]?.ziel_ordner && !inFlightPaths.has(d.path));
      setInFlightPaths((prev) => { const n = new Set(prev); files.forEach((d) => n.add(d.path)); return n; });
      await submitJob(files.map((d) => buildJobItem(d)));
      setCheckedPaths(new Set());
      setBatchMoving(false);
      return;
    }

    // ── Bulk-Modus: alle grünen/manuellen auf einmal (kein Dup-Popup) ──
    const toProcess = batchReady;
    // Sofort sichtbar: Zeilen ausgrauen + Status in der Ecke
    setInFlightPaths((prev) => { const n = new Set(prev); toProcess.forEach((d) => n.add(d.path)); return n; });
    setJobVorlauf("Duplikat-Prüfung läuft…");
    try {
      const checkItems = toProcess.map((d) => {
        const s = fileSettings[d.path];
        return { quell_pfad: d.path, ziel_ordner: s.ziel_ordner, name: d.name };
      });
      const dups = await checkBatchDuplikate(checkItems);
      const dupPaths = new Set(dups.map((d) => d.quell?.quell_pfad));
      if (dups.length > 0) setDuplikatList(dups);
      // Duplikate bleiben in der Liste → wieder aktiv
      setInFlightPaths((prev) => { const n = new Set(prev); dupPaths.forEach((p) => n.delete(p)); return n; });

      const nonDupReady = toProcess.filter((d) => !dupPaths.has(d.path));
      if (nonDupReady.length === 0) return;

      await submitJob(nonDupReady.map((d) => buildJobItem(d)));  // Hintergrund-Job
    } finally {
      setJobVorlauf("");
      setBatchMoving(false);
    }
  }, [batchToProcess, batchReady, checkedPaths, fileSettings, dateien, checkDuplikat, _doMove, _buildName, checkBatchDuplikate, buildJobItem, submitJob, inFlightPaths]);

  /* == Dropbox Scan entfernt — Analyse macht jetzt alles == */

  /* == Counts == */
  const greenCount   = dateien.filter((d) => fileSettings[d.path]?.confidence === "green").length;
  const yellowCount  = dateien.filter((d) => fileSettings[d.path]?.confidence === "yellow").length;
  const redCount     = dateien.filter((d) => fileSettings[d.path]?.confidence === "red").length;
  const autoCount    = dateien.filter((d) => _effectiveConf(fileSettings[d.path]) === "auto").length;
  const manuellCount = dateien.filter((d) => fileSettings[d.path]?.confidence === "manuell").length;

  /* ======= RENDER ======= */

  if (phase === "loading" || phase === "analysing") {
    return (
      <div className="verschieben-page bilder-page">
        <div className="v-loading-screen">
          <div className="v-loading-anim">
            <div className="v-pulse-ring" />
            {phase === "analysing" ? <FaSearch className="v-loading-icon" /> : <FaCamera className="v-loading-icon" />}
          </div>
          <div className="v-loading-text">{loadMsg}</div>
          <div className="v-loading-sub">
            {phase === "analysing" ? "Barcode + OCR + Vertrags-Matching..." : "Zählerstände aus Dropbox-Inbox laden..."}
          </div>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="verschieben-page bilder-page">
        <div className="v-error-screen">
          <FaTimes className="v-error-icon" />
          <h3>Fehler beim Laden</h3>
          <p>{error}</p>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="v-btn primary" onClick={() => loadDateien()}>Erneut versuchen</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verschieben-page bilder-page">
      {/* Header */}
      <div className="v-header">
        <button className="v-btn ghost" onClick={() => navigate(location.state?.returnTo || "/")}>← {location.state?.returnTo ? "Zurück" : "Startseite"}</button>
        <h2 className="v-title"><FaCamera style={{ marginRight: 6 }} /> {modus === "umbenennen" ? "Zählerstände umbenennen" : "Zählerstände verschieben"}</h2>
        {analysed && (
          <div className="v-stats">
            {autoCount > 0 && <span className="v-tag" style={{ background: "#0d2e2e", border: "1px solid #00bcd4", color: "#4dd0e1" }}>⚡ {autoCount}</span>}
            <span className="v-tag green">{greenCount}</span>
            <span className="v-tag yellow">{yellowCount}</span>
            <span className="v-tag red">{redCount}</span>
            {manuellCount > 0 && <span className="v-tag" style={{ background: "#2d1a40", border: "1px solid #9c27b0", color: "#ce93d8" }}>M {manuellCount}</span>}
          </div>
        )}
        <span className="v-tag total">{dateien.length} Zählerstände</span>
        <div className="v-header-actions">
          {!analysed ? (
            <button className="v-btn analyse" onClick={nurDateien ? () => {
              (async () => {
                setPhase("analysing");
                setLoadMsg(`Analyse läuft (${dateien.length} Dateien)...`);
                for (const d of dateien) { try { await analyseEinzel(d.path); } catch (_) {} }
                setAnalysed(true);
                setPhase("ready");
              })();
            } : runAnalyse} title="Barcode + OCR + Matching starten">
              <FaSearch /> Analyse starten
            </button>
          ) : (
            <button className="v-btn ghost small"
              onClick={() => setEnginePickerFile({ alle: true, anzahl: checkedPaths.size > 0 ? checkedPaths.size : dateien.length })}
              title={checkedPaths.size > 0 ? `${checkedPaths.size} markierte Zeilen neu analysieren — Engine wählen` : "Alle neu analysieren — Engine wählen"}>
              <FaSync /> Neu analysieren{checkedPaths.size > 0 ? ` (${checkedPaths.size})` : ""}
            </button>
          )}
          <button className="v-btn ghost" onClick={() => loadDateien()} title="Nur Dateiliste neu laden"><FaSync /></button>
          {geminiStatus?.key_vorhanden && (
            <button className="v-btn ghost small" onClick={toggleGemini}
              style={{ color: geminiStatus.enabled ? "#ffd54f" : "#888", borderColor: geminiStatus.enabled ? "#ffd54f" : undefined }}
              title={geminiStatus.enabled
                ? "Gemini-KI-Erkennung + Automatik AN — sichere Zähler werden automatisch verarbeitet (Klick = aus)"
                : "Gemini-KI-Erkennung AUS (Klick = an)"}>
              ✨ KI {geminiStatus.enabled ? "an" : "aus"}
            </button>
          )}
          {checkedPaths.size > 0 && (
            <div className="v-header-marked">
              <button className="v-btn primary" onClick={() => setShowBatch(true)} disabled={batchMoving} title={modus === "umbenennen" ? "Markierte Zählerstände umbenennen" : "Markierte Zählerstände verschieben (mit Duplikat-Check)"}>
                <FaCheck /> {modus === "umbenennen" ? "Umbenennen" : "Verschieben"} ({checkedPaths.size})
              </button>
              {modus !== "umbenennen" && <button className="v-btn" style={{ background: "#5c3d00", color: "#ffb74d", border: "1px solid #ff9800" }} onClick={async () => {
                if (!window.confirm(`${checkedPaths.size} markierte Bild(er) in NOK verschieben?`)) return;
                // Hintergrund-Job wie jedes Verschieben; Zeilen verschwinden bei ok
                await submitJob([...checkedPaths].map((p) => ({ quell_pfad: p, ziel_ordner: "", nok_original: true })));
                setCheckedPaths(new Set());
              }} title="Markierte in NOK-Ordner">
                NOK verschieben ({checkedPaths.size})
              </button>}
              {modus !== "umbenennen" && <button className="v-btn" style={{ background: "#1a3060", color: "#90caf9", border: "1px solid #42a5f5" }} onClick={async () => {
                if (!window.confirm(`${checkedPaths.size} markierte Bild(er) in Fotos-Inbox verschieben?`)) return;
                for (const p of checkedPaths) {
                  try { await apiPost("/fotos/nach-fotos", { pfad: p }); } catch {}
                }
                setDateien((prev) => prev.filter((d) => !checkedPaths.has(d.path)));
                setCheckedPaths(new Set());
              }} title="Markierte in Fotos-Inbox">
                In Fotos verschieben ({checkedPaths.size})
              </button>}
              <button className="v-btn danger" onClick={async () => {
                if (!window.confirm(`${checkedPaths.size} markierte Bild(er) wirklich löschen?`)) return;
                for (const p of checkedPaths) {
                  try { await apiPost("/bilder/loeschen", { pfad: p }); } catch {}
                }
                setDateien((prev) => prev.filter((d) => !checkedPaths.has(d.path)));
                setCheckedPaths(new Set());
              }} title="Markierte löschen">
                <FaTrash /> Löschen ({checkedPaths.size})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Source Info Bar */}
      <div className="v-source-bar">
        <FaFolder className="v-source-icon" />
        <span className="v-source-label">Quell-Ordner:</span>
        <span className="v-source-path" title={inbox}>{shortPath(inbox) || inbox}</span>
      </div>

      {/* OCR Status Bar */}
      {ocrStatus && (
        <div className="v-ocr-status" style={{
          display: "flex", alignItems: "center", gap: 10, padding: "4px 12px",
          fontSize: 12, borderRadius: 6, margin: "0 0 4px",
          background: ocrStatus.state === "offline" ? "#3b1010"
            : ocrStatus.state === "idle" ? "#0d1f0d"
            : ocrStatus.state === "batch" ? "#0d1a2e"
            : ocrStatus.state === "einzel" ? "#2e2a0d" : "#1a1a2e",
          border: `1px solid ${ocrStatus.state === "offline" ? "#c62828"
            : ocrStatus.state === "idle" ? "#2e7d32"
            : ocrStatus.state === "batch" ? "#1565c0"
            : ocrStatus.state === "einzel" ? "#f9a825" : "#444"}`,
          color: ocrStatus.state === "offline" ? "#ef9a9a"
            : ocrStatus.state === "idle" ? "#81c784"
            : ocrStatus.state === "batch" ? "#90caf9"
            : ocrStatus.state === "einzel" ? "#fff176" : "#888",
        }}>
          <span style={{ fontSize: 14 }}>
            {ocrStatus.state === "offline" ? "🔴" : ocrStatus.state === "idle" ? "🟢" : ocrStatus.state === "batch" ? "🔵" : ocrStatus.state === "einzel" ? "🟡" : "⚪"}
          </span>
          <span style={{ fontWeight: 600 }}>
            {ocrStatus.state === "offline" ? "OCR Server offline"
              : ocrStatus.state === "idle" ? "OCR idle"
              : ocrStatus.state === "batch" ? `OCR Batch: ${ocrStatus.progress || "..."}`
              : ocrStatus.state === "einzel" ? "OCR Einzel"
              : `OCR: ${ocrStatus.state}`}
          </span>
          {ocrStatus.current_file && ocrStatus.state !== "idle" && ocrStatus.state !== "offline" && (
            <span style={{ opacity: 0.7 }}>— {ocrStatus.current_file}</span>
          )}
          {(ocrStatus.queue_priority > 0 || ocrStatus.queue_batch > 0) && (
            <span style={{ opacity: 0.6 }}>
              | Queue: {ocrStatus.queue_priority > 0 ? `${ocrStatus.queue_priority} prio` : ""}{ocrStatus.queue_priority > 0 && ocrStatus.queue_batch > 0 ? " + " : ""}{ocrStatus.queue_batch > 0 ? `${ocrStatus.queue_batch} batch` : ""}
            </span>
          )}
          {ocrStatus.last_error_file && (
            <span style={{ opacity: 0.5, marginLeft: "auto" }} title={ocrStatus.last_error}>⚠ {ocrStatus.last_error_file}</span>
          )}
        </div>
      )}

      <div className="v-keyboard-hint">
        <FaArrowUp /> <FaArrowDown /> navigieren
      </div>

      {/* Split View */}
      <div className="v-split">
        {/* LEFT: File List */}
        <div className="v-left-col">
          <div className="v-filter-bar">
            <FaSearch className="v-filter-icon" />
            <input
              className="v-filter-input"
              style={{ flex: "none", width: 90 }}
              placeholder="Prefix..."
              value={filterPrefix}
              onChange={e => setFilterPrefix(e.target.value)}
            />
            <input
              className="v-filter-input"
              style={{ flex: "none", width: 100 }}
              type="date"
              value={filterDatum}
              onChange={e => setFilterDatum(e.target.value)}
            />
            <input
              className="v-filter-input"
              placeholder="Freitext (Leerzeichen = UND)..."
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
            />
            <select value={filterArt} onChange={e => setFilterArt(e.target.value)} className="v-quick-select" style={{ maxWidth: 100 }}>
              <option value="">Alle Arten</option>
              {zaehlerArten.map(a => <option key={a} value={a}>{a}</option>)}
              <option value="__none__">Ohne Art</option>
            </select>
            {(filterArt || filterPrefix || filterDatum || filterText) && (
              <button className="v-btn ghost small" onClick={() => { setFilterArt(""); setFilterPrefix(""); setFilterDatum(""); setFilterText(""); }} title="Filter löschen">
                <FaTimes />
              </button>
            )}
            <span className="v-filter-count">{filteredDateien.length}/{dateien.length}</span>
          </div>
          <div className="v-file-list" ref={listRef}>
          {dateien.length === 0 ? (
            <div className="v-empty">Keine Zählerstände im Inbox-Ordner.</div>
          ) : (() => {
            const grouped = {};
            filteredDateien.forEach((d) => {
              const origIdx = dateien.indexOf(d);
              const conf = lockedGroups[d.path] || _effectiveConf(fileSettings[d.path]);
              if (!grouped[conf]) grouped[conf] = [];
              grouped[conf].push({ d, origIdx });
            });

            const toggleGroup = (key) => {
              setCollapsedGroups(prev => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key); else next.add(key);
                return next;
              });
            };

            return GROUP_ORDER.filter(g => grouped[g.key]?.length > 0).map(g => {
              const items = grouped[g.key];
              const gc = CONF[g.key] || CONF.none;
              const isCollapsed = collapsedGroups.has(g.key);
              const groupPaths = items.map(i => i.d.path);
              const allChecked = groupPaths.length > 0 && groupPaths.every(p => checkedPaths.has(p));
              const someChecked = groupPaths.some(p => checkedPaths.has(p));
              return (
                <div key={g.key} className="v-group">
                  <div className="v-group-header-row" style={{ borderLeftColor: gc.border }}>
                    <button
                      className="v-group-header"
                      style={{ color: gc.text }}
                      onClick={() => toggleGroup(g.key)}
                    >
                      <span className="v-group-icon">{g.icon}</span>
                      <span className="v-group-label">{g.label}</span>
                      <span className="v-group-count">{items.length}</span>
                    </button>
                    <div className="v-group-batch" onClick={(e) => e.stopPropagation()}>
                      <input
                        className="v-group-batch-input"
                        placeholder="Zählernr…"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.target.value.trim()) {
                            const val = e.target.value.trim();
                            const targets = someChecked ? groupPaths.filter(p => checkedPaths.has(p)) : groupPaths;
                            targets.forEach(p => updateSetting(p, "zaehlernummer", val));
                            e.target.value = "";
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        title="Zählernummer eingeben + Enter"
                      />
                      <select
                        className="v-group-batch-select"
                        value=""
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) return;
                          const targets = someChecked ? groupPaths.filter(p => checkedPaths.has(p)) : groupPaths;
                          targets.forEach(p => updateSetting(p, "art", val));
                          e.target.value = "";
                        }}
                        title="Art setzen"
                      >
                        <option value="">Art…</option>
                        {zaehlerArten.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <select
                        className="v-group-batch-select"
                        value=""
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) return;
                          const targets = someChecked ? groupPaths.filter(p => checkedPaths.has(p)) : groupPaths;
                          targets.forEach(p => updateSetting(p, "obis_code", val));
                          e.target.value = "";
                        }}
                        title="OBIS setzen"
                      >
                        <option value="">OBIS…</option>
                        {["1.8.0","1.8.1","1.8.2","2.8.0","2.8.1","2.8.2"].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <input
                      type="checkbox"
                      className="v-group-checkbox"
                      checked={allChecked}
                      ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                      onChange={() => {
                        setCheckedPaths(prev => {
                          const n = new Set(prev);
                          if (allChecked) { groupPaths.forEach(p => n.delete(p)); }
                          else { groupPaths.forEach(p => n.add(p)); }
                          return n;
                        });
                      }}
                      onClick={(e) => e.stopPropagation()}
                      title={`Alle ${items.length} ${g.label} auswählen`}
                    />
                    <span
                      className={"v-group-chevron" + (isCollapsed ? " collapsed" : "")}
                      onClick={() => toggleGroup(g.key)}
                      style={{ cursor: "pointer", color: gc.text }}
                    >▼</span>
                  </div>
                  {!isCollapsed && items.map(({ d, origIdx: idx }) => {
                    const s = fileSettings[d.path] || {};
                    const conf = _effectiveConf(s);
                    const c = CONF[conf] || CONF.none;
                    const isSel = idx === selectedIdx;
                    const inFlight = inFlightPaths.has(d.path);
                    return (
                      <div
                        key={d.path}
                        ref={(el) => (cardRefs.current[idx] = el)}
                        className={"v-card" + (isSel ? " active" : "")}
                        style={{ borderLeftColor: c.border, ...(inFlight ? { opacity: 0.45, pointerEvents: "none" } : {}) }}
                        title={inFlight ? "Wird gerade verschoben…" : undefined}
                      onClick={() => { setSelectedIdx(idx); setMoveResult(null); }}
                        onClickCapture={() => { setSelectedIdx(idx); setMoveResult(null); }}
                      >
                        {/* Row 1: Prefix-Dropdown + Dateiname + NOK */}
                        <div className="v-card-row1">
                          <select
                            className="v-prefix-select"
                            value={s.prefix || ""}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); updatePrefix(d.path, e.target.value); }}
                            title="Objekt-Einheit (Prefix)"
                          >
                            <option value="">– Prefix –</option>
                            {/* Erkannten Prefix als Option hinzufügen wenn nicht in Liste */}
                            {s.prefix && !katalogEinheiten.some((e) => e.einheit_nr === s.prefix) && (
                              <option value={s.prefix}>✨ {s.prefix} (erkannt)</option>
                            )}
                            {katalogEinheiten.map((e) => (
                              <option key={e.einheit_nr} value={e.einheit_nr}>
                                {e.einheit_nr}{e.zaehler_arten ? ` – ${e.zaehler_arten}` : ""}
                              </option>
                            ))}
                          </select>
                          {editingPath === d.path ? (
                            <input
                              className="v-fname-edit"
                              autoFocus
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.stopPropagation(); handleRename(d.path, editingName); }
                                if (e.key === "Escape") { e.stopPropagation(); setEditingPath(null); }
                              }}
                              onBlur={() => handleRename(d.path, editingName)}
                            />
                          ) : (
                            <span
                              {...dragOutProps(d.path, d.name)}
                              {...contextShareProps(d.path, d.name)}
                              className="v-fname"
                              title={"Klicken zum Umbenennen — Rausziehen = Datei kopieren, Rechtsklick = Teilen: " + d.name}
                              onClick={(e) => { e.stopPropagation(); setEditingPath(d.path); setEditingName(d.name); }}
                            >{d.name}</span>
                          )}
                          {/* Welle 65: Gemini-Badge + KI-Erkennen-Button */}
                          {(s.quelle || "").includes("gemini") && (
                            <span className="b-ocr-badge" style={{ color: "#ffd54f" }}
                              title={"Von Gemini-KI erkannt" + (s.beschriftung ? ` — Beschriftung: ${s.beschriftung}` : "") + (s.plausibilitaet ? ` — ${s.plausibilitaet}` : "")}>
                              ✨ KI
                            </span>
                          )}
                          {/* OCR-Cache Status Badge */}
                          {s.cache_status === "fehler" && (
                            <span className="b-ocr-badge fehler" title="OCR-Cache: Fehler bei letzter Analyse">⚠ OCR</span>
                          )}
                          {(s.cache_status === "bearbeitung" || rescanningPath === d.path) && (
                            <span className="b-ocr-badge pending" title="Analyse läuft...">⏳</span>
                          )}
                          <div className="v-card-checks">
                            {modus !== "umbenennen" && <>
                            <label className="v-mini-check nok" title={s.nok_original ? "NOK — Original direkt in Fehler-Ordner" : "NOK — Kopie in Fehler-Ordner"}>
                              <input type="checkbox" checked={(s.kopie_nok || s.nok_original) ? true : false}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (s.kopie_nok || s.nok_original) {
                                    setFileSettings(prev => ({ ...prev, [d.path]: { ...prev[d.path], kopie_nok: false, nok_original: false } }));
                                  } else {
                                    setNokPickerFile(d);
                                  }
                                }} />
                              <span style={{ color: s.nok_original ? "#f97316" : s.kopie_nok ? "#ef4444" : undefined }}>{s.nok_original ? "NOK!" : "NOK"}</span>
                            </label>
                            <label className="v-mini-check v-trello-check" title="Trello-Card erstellen">
                              <input type="checkbox" checked={s.trello_enabled || false}
                                onChange={(e) => { e.stopPropagation(); if (!s.trello_enabled) { openTrelloPopup(d.path); } else { setFileSettings(prev => ({ ...prev, [d.path]: { ...prev[d.path], trello_enabled: false } })); } }} />
                              <span style={{ color: s.trello_enabled ? "#9c27b0" : undefined }}>Trello</span>
                            </label>
                            <label className="v-mini-check" title="Zählerstand in Excel eintragen (VBA-Sync)">
                              <input type="checkbox" checked={s.excel_eintrag || false}
                                onChange={(e) => { e.stopPropagation(); setFileSettings(prev => ({ ...prev, [d.path]: { ...prev[d.path], excel_eintrag: !s.excel_eintrag } })); }} />
                              <span style={{ color: s.excel_eintrag ? "#22c55e" : undefined }}>Excel</span>
                            </label>
                            </>}
                          </div>
                          {duplikatWarnungen[d.path] && (
                            <span
                              className="v-duplikat-badge"
                              title={`Duplikat im Zielordner: ${duplikatWarnungen[d.path].existierend?.name || "?"}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const warn = duplikatWarnungen[d.path];
                                const nextPathOnDel = _nextVisualPath(d.path);
                                setDuplikatInfo({
                                  quell: { name: d.name || d.path.split("/").pop(), path: d.path, size: warn.quell?.size, vorschau_url: warn.quell?.vorschau_url },
                                  existierend: warn.existierend,
                                  duplikat_typ: warn.duplikat_typ,
                                  zielOrdner: fileSettings[d.path]?.ziel_ordner,
                                  onDelete: async () => {
                                    try {
                                      await apiPost("/bilder/loeschen", { pfad: d.path });
                                      setDateien((prev) => {
                                        const next = prev.filter((x) => x.path !== d.path);
                                        const nextIdx = nextPathOnDel ? next.findIndex((x) => x.path === nextPathOnDel) : -1;
                                        setSelectedIdx(nextIdx >= 0 ? nextIdx : Math.max(0, next.length - 1));
                                        return next;
                                      });
                                      setCheckedPaths((prev) => { const n = new Set(prev); n.delete(d.path); return n; });
                                      setDuplikatWarnungen((prev) => { const n = { ...prev }; delete n[d.path]; return n; });
                                      setMoveResult({ ok: true, msg: "Duplikat gelöscht" });
                                    } catch (err) {
                                      setMoveResult({ ok: false, msg: "Löschen fehlgeschlagen: " + err.message });
                                    }
                                    setDuplikatInfo(null);
                                  },
                                  onReplace: async () => {
                                    try {
                                      await apiPost("/bilder/loeschen", { pfad: warn.existierend.path });
                                    } catch (err) {
                                      setMoveResult({ ok: false, msg: "Alte Datei löschen fehlgeschlagen: " + err.message });
                                      setDuplikatInfo(null);
                                      return;
                                    }
                                    setDuplikatWarnungen((prev) => { const n = { ...prev }; delete n[d.path]; return n; });
                                    setDuplikatInfo(null);
                                    setMoveResult({ ok: true, msg: "Alte Datei gelöscht — wird beim Verschieben ersetzt" });
                                  },
                                  onBeideBehalten: () => {
                                    setDuplikatWarnungen((prev) => { const n = { ...prev }; delete n[d.path]; return n; });
                                    setDuplikatInfo(null);
                                  },
                                  onAbort: () => setDuplikatInfo(null),
                                });
                              }}
                            >
                              ⚠️ Duplikat
                            </span>
                          )}
                        </div>

                        {/* Row 2: Zählernr + Such-Button + Rescan + Art + Datum + OBIS + Stand + Confidence */}
                        <div className="v-card-row2 bilder-row2" style={{ position: "relative" }}>
                          {/* Zähler: Editierbares Textfeld + Dropdown-Vorschläge aus Katalog */}
                          <input
                            className="v-zaehler-input"
                            type="text"
                            list={`zaehler-list-${idx}`}
                            placeholder="Zähler-Nr"
                            value={s.zaehlernummer || ""}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              const znr = e.target.value;
                              const normZnr = znr.replace(/[\s\-\.]/g, "").toUpperCase();
                              const found = s._katalog_zaehler?.find((z) =>
                                z.zaehlernummer === znr ||
                                z.zaehlernummer.replace(/[\s\-\.]/g, "").toUpperCase() === normZnr
                              );
                              if (found) {
                                // User hat Datalist-Vorschlag gewählt → direkt Pfad auflösen
                                const artMap = { "Strom": "Strom", "Gas": "Gas", "Wasser": "Wasser", "Kaltwasser": "Wasser", "Warmwasser": "Wasser", "Heizung": "Heizung" };
                                setFileSettings((prev) => ({
                                  ...prev,
                                  [d.path]: {
                                    ...prev[d.path],
                                    zaehlernummer: znr,
                                    art: artMap[found.zaehler_art] || prev[d.path]?.art || "",
                                    reason: `Katalog: ${znr} (${found.zaehler_art}${found.stand != null ? `, Stand: ${found.stand}` : ""})`,
                                  },
                                }));
                                selectZaehlerResult(d.path, { zaehlernummer_db: znr });
                              } else {
                                updateSetting(d.path, "zaehlernummer", znr);
                                // Live-Vorschläge: Katalog-API mit Debounce anfragen
                                clearTimeout(zaehlerSuchDebounceRef.current);
                                if (znr.length >= 3) {
                                  zaehlerSuchDebounceRef.current = setTimeout(async () => {
                                    try {
                                      const res = await fetch(API_BASE + "/zuordnen/api/bilder/katalog-suche?q=" + encodeURIComponent(znr));
                                      const data = await res.json();
                                      const results = data.results || [];
                                      if (results.length > 0) {
                                        setFileSettings((prev) => ({
                                          ...prev,
                                          [d.path]: {
                                            ...prev[d.path],
                                            _katalog_zaehler: results.map((r) => ({
                                              zaehlernummer: r.zaehlernummer_db,
                                              zaehler_art: r.art || "",
                                              stand: null,
                                            })),
                                          },
                                        }));
                                      }
                                    } catch {}
                                  }, 350);
                                }
                              }
                            }}
                            onBlur={() => {
                              saveToCache(d.path);
                              if (s.zaehlernummer && !s.ziel_ordner) matchZaehler(d.path);
                            }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
                            title={(() => { const k = katalogKandidaten(s._katalog_zaehler, s.art); return k.length >= 1
                              ? `Katalog (${k.length}): ${k.map((z) => z.zaehlernummer).join(", ")}`
                              : "Zählernummer"; })()}
                          />
                          {!s.zaehlernummer && katalogKandidaten(s._katalog_zaehler, s.art).length > 1 && (
                            <select
                              className="v-zaehler-input"
                              style={{ maxWidth: 190, marginLeft: 4 }}
                              value=""
                              title="Mehrere Zähler im Katalog für dieses Objekt — bitte wählen"
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                const znr = e.target.value;
                                const z = (s._katalog_zaehler || []).find((k) => k.zaehlernummer === znr);
                                if (!z) return;
                                setFileSettings((prev) => ({
                                  ...prev,
                                  [d.path]: {
                                    ...prev[d.path],
                                    zaehlernummer: znr,
                                    art: prev[d.path]?.art || KATALOG_ART_MAP[z.zaehler_art] || "",
                                    obis_code: prev[d.path]?.obis_code || z.obis_register || "1.8.0",
                                    reason: (prev[d.path]?.reason || "") + ` | Zähler ${znr} gewählt (${z.zaehler_art})`,
                                  },
                                }));
                                selectZaehlerResult(d.path, { zaehlernummer_db: znr });
                              }}
                            >
                              <option value="">Zähler wählen… ({katalogKandidaten(s._katalog_zaehler, s.art).length})</option>
                              {katalogKandidaten(s._katalog_zaehler, s.art).map((z) => (
                                <option key={z.zaehlernummer} value={z.zaehlernummer}>
                                  {z.zaehlernummer} · {z.zaehler_art}{z.einbauort ? ` · ${z.einbauort}` : ""}{z.obis_register && z.obis_register !== "1.8.0" ? ` · ${z.obis_register}` : ""}
                                </option>
                              ))}
                            </select>
                          )}
                          {katalogKandidaten(s._katalog_zaehler, s.art).length > 0 && (
                            <datalist id={`zaehler-list-${idx}`}>
                              {katalogKandidaten(s._katalog_zaehler, s.art).map((z) => (
                                <option key={z.zaehlernummer} value={z.zaehlernummer}>
                                  {z.zaehler_art}{z.stand != null ? ` | Stand: ${z.stand}` : ""}
                                </option>
                              ))}
                            </datalist>
                          )}
                          <button
                            className={"v-gen-name-btn" + (matchingPath === d.path ? " loading" : "")}
                            title="Zählernummer in DB suchen"
                            disabled={matchingPath === d.path || !s.zaehlernummer}
                            onClick={(e) => { e.stopPropagation(); matchZaehler(d.path); }}
                          >
                            {matchingPath === d.path ? <FaSync className="spin" /> : <FaSearch />}
                          </button>
                          <select
                            className="v-art-select"
                            value={s.art || ""}
                            tabIndex={-1}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); updateSetting(d.path, "art", e.target.value); }}
                            title="Art (Strom/Gas/Wasser)"
                          >
                            <option value="">Art</option>
                            {zaehlerArten.map((a) => <option key={a} value={a}>{a}</option>)}
                          </select>
                          <input
                            className="v-datum-input"
                            type="date"
                            value={s.foto_datum || ""}
                            tabIndex={-1}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); updateSetting(d.path, "foto_datum", e.target.value); }}
                            title="Foto-Datum"
                          />
                                          {(s.art === "Strom" || s.art === "Zwischenzähler" || s.art === "PVStrom" || s.art === "Zwischenzähler") && (
                            (d.path in obisFreitext)
                              ? <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                  <input
                                    className="v-obis-select has-value"
                                    style={{ width: 52 }}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={3}
                                    placeholder="181"
                                    autoFocus
                                    value={obisFreitext[d.path]}
                                    tabIndex={isSel ? 0 : -1}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={async (e) => {
                                      e.stopPropagation();
                                      const digits = e.target.value.replace(/\D/g, "").slice(0, 3);
                                      setObisFreitext((prev) => ({ ...prev, [d.path]: digits }));
                                      if (digits.length === 3) {
                                        const newObis = `${digits[0]}.${digits[1]}.${digits[2]}`;
                                        updateSetting(d.path, "obis_code", newObis);
                                        setObisFreitext((prev) => { const n = { ...prev }; delete n[d.path]; return n; });
                                        const znr = s.zaehlernummer?.trim();
                                        if (znr && modus !== "umbenennen") {
                                          try {
                                            const res = await apiPost("/bilder/match-zaehler", {
                                              zaehlernummer: znr, bild_pfad: d.path, obis_code: newObis
                                            });
                                            if (res.ok && res.ziel_ordner) updateSetting(d.path, "ziel_ordner", res.ziel_ordner);
                                          } catch (_e) { /* silent */ }
                                        }
                                      }
                                    }}
                                    title="3 Ziffern eingeben, z.B. 181 → 1.8.1"
                                  />
                                  <button
                                    style={{ background: "none", border: "none", color: "#888", cursor: "pointer", padding: "0 2px", fontSize: 11 }}
                                    title="Abbrechen"
                                    onClick={(e) => { e.stopPropagation(); setObisFreitext((prev) => { const n = { ...prev }; delete n[d.path]; return n; }); }}
                                  >×</button>
                                </span>
                              : <select
                                  className="v-obis-select has-value"
                                  value={s.obis_code || (s.art === "Zwischenzähler" ? "ZWZ" : "1.8.0")}
                                  tabIndex={isSel ? 0 : -1}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={async (e) => {
                                    e.stopPropagation();
                                    const newObis = e.target.value;
                                    if (newObis === "__freitext__") {
                                      setObisFreitext((prev) => ({ ...prev, [d.path]: "" }));
                                      return;
                                    }
                                    updateSetting(d.path, "obis_code", newObis);
                                    const znr = s.zaehlernummer?.trim();
                                    if (znr && modus !== "umbenennen") {
                                      try {
                                        const res = await apiPost("/bilder/match-zaehler", {
                                          zaehlernummer: znr, bild_pfad: d.path, obis_code: newObis
                                        });
                                        if (res.ok && res.ziel_ordner) updateSetting(d.path, "ziel_ordner", res.ziel_ordner);
                                      } catch (_e) { /* silent */ }
                                    }
                                  }}
                                  title="OBIS-Code"
                                >
                                  {(() => {
                                    const STD = ["1.8.0","1.8.1","1.8.2","1.8.3","2.8.0","2.8.1","2.8.2","2.8.3","3.8.0","4.8.0","4.8.1","4.8.2","4.8.3","5.8.0","6.8.0","7.8.0","8.8.0"];
                                    const cur = s.obis_code;
                                    const extra = cur && cur !== "ZWZ" && !STD.includes(cur) ? cur : null;
                                    return <>
                                      <option value="ZWZ">ZWZ</option>
                                      {STD.map((c) => <option key={c} value={c}>{c}</option>)}
                                      {extra && <option key={extra} value={extra}>{extra}</option>}
                                      <option value="__freitext__">— Freitext …</option>
                                    </>;
                                  })()}
                                </select>
                          )}
                          <input
                            className="v-stand-input"
                            type="text"
                            placeholder="Stand"
                            value={s.stand || ""}
                            ref={el => { standInputRefs.current[d.path] = el; }}
                            tabIndex={isSel ? 0 : -1}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); updateSetting(d.path, "stand", e.target.value); }}
                            onBlur={() => saveToCache(d.path)}
                            onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); handleMove(d); } }}
                            title={s._stand_aus_cache ? "Zählerstand aus OCR/KI-Cache übernommen — bitte prüfen" : "Zählerstand (Pflichtfeld)"}
                            style={(!s.stand && s.zaehlernummer) ? { borderColor: "#c62828", boxShadow: "0 0 0 1px #c62828" }
                                   : (s._stand_aus_cache ? { borderColor: "#f9a825", boxShadow: "0 0 0 1px #f9a825" } : undefined)}
                          />
                          {s._letzter_stand != null && (() => {
                            const cur = parseFloat(String(s.stand || "").replace(",", "."));
                            const kleiner = !Number.isNaN(cur) && cur < s._letzter_stand;
                            const dat = s._letzter_stand_datum ? String(s._letzter_stand_datum).slice(0, 10).split("-").reverse().join(".") : "";
                            return (
                              <span className={"v-letzter-stand" + (kleiner ? " warn" : "")}
                                title={`Letzter bekannter Stand dieses Zählers: ${s._letzter_stand}${dat ? " am " + dat : ""}${kleiner ? " — neuer Stand ist KLEINER, bitte prüfen" : ""}${s.plausibilitaet ? "\n" + s.plausibilitaet : ""}`}>
                                ↩ {s._letzter_stand.toLocaleString("de-DE")}{dat ? ` (${dat.slice(0, 5)})` : ""}
                              </span>
                            );
                          })()}
                          <div className="v-conf-icons">
                            {[
                              { val: "green",   icon: "●", title: "Sicher" },
                              { val: "yellow",  icon: "◐", title: "Möglich" },
                              { val: "red",     icon: "○", title: "Unbekannt" },
                              { val: "white",   icon: "◇", title: "Nicht in DB" },
                              { val: "manuell", icon: "M", title: "Manuell (kein DB-Eintrag)" },
                            ].map(({ val, icon, title }) => (
                              <button
                                key={val}
                                tabIndex={-1}
                                className={"v-conf-icon-btn" + (conf === val ? " active" : "")}
                                style={conf === val ? { color: CONF[val].text, borderColor: CONF[val].border } : {}}
                                title={title}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (val === "manuell") {
                                    setFileSettings((prev) => ({
                                      ...prev,
                                      [d.path]: { ...prev[d.path], confidence: "manuell", ziel_ordner: MANUELL_ZIEL },
                                    }));
                                  } else {
                                    setFileSettings((prev) => ({ ...prev, [d.path]: { ...prev[d.path], confidence: val } }));
                                  }
                                }}
                              >{icon}</button>
                            ))}
                          </div>
                          {/* Zähler-Suche Dropdown */}
                          {zaehlerSuchPath === d.path && zaehlerSuchResults.length > 0 && (
                            <div
                              className="v-zaehler-such-dropdown"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
                                background: "#1e1e2e", border: "1px solid #444", borderRadius: 6,
                                boxShadow: "0 8px 24px rgba(0,0,0,.5)", maxHeight: 260, overflowY: "auto",
                              }}
                            >
                              {zaehlerSuchResults.map((r, ri) => (
                                <button
                                  key={ri}
                                  className="v-zaehler-such-item"
                                  onClick={() => selectZaehlerResult(d.path, r)}
                                  style={{
                                    display: "flex", gap: 8, alignItems: "center", width: "100%",
                                    padding: "7px 10px", border: "none", borderBottom: "1px solid #333",
                                    background: "transparent", color: "#ddd", cursor: "pointer", textAlign: "left",
                                    fontSize: "0.82rem",
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = "#2a2a40"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                >
                                  <span style={{ fontWeight: 700, minWidth: 140 }}>{r.zaehlernummer_db}</span>
                                  <span style={{ color: "#888", minWidth: 55 }}>{r.art}</span>
                                  <span style={{ color: "#666", minWidth: 65 }}>{r.einheit_nr || "–"}</span>
                                  <span style={{ color: "#555", fontSize: "0.75rem" }}>{r.score}%</span>
                                </button>
                              ))}
                              <button
                                className="v-zaehler-such-item"
                                onClick={() => {
                                  setZaehlerSuchResults([]);
                                  setZaehlerSuchPath(null);
                                  setAnlegenModal({ path: d.path, zaehlernummer: s.zaehlernummer || "" });
                                }}
                                style={{
                                  display: "flex", gap: 8, alignItems: "center", width: "100%",
                                  padding: "8px 10px", border: "none", background: "transparent",
                                  color: "#4caf50", cursor: "pointer", textAlign: "left",
                                  fontWeight: 700, fontSize: "0.82rem",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#1a2e1a"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                              >
                                + Neuer Eintrag anlegen
                              </button>
                            </div>
                          )}
                        </div>



                        {/* Row 3: Ziel + Browse + Analyse + Move + Check + Delete */}
                        <div className="v-card-row3">
                          {modus === "umbenennen" && (() => {
                            const preview = _buildName(d.path, s);
                            const reason = s.reason || "";
                            return (
                              <div className="v-rename-preview" title={preview || "Noch keine Vorschau"}>
                                {reason === "⏳ OCR läuft…"
                                  ? <span className="v-ocr-pending">⏳ OCR läuft…</span>
                                  : preview
                                    ? <span className="v-preview-name">→ {preview}</span>
                                    : <span className="v-preview-empty">Keine Vorschau</span>
                                }
                              </div>
                            );
                          })()}
                          {modus !== "umbenennen" && <>
                          <FaArrowRight className="v-arrow-icon" style={{ color: c.text }} />
                          <div className="v-input-copy-wrap">
                            <input
                              className="v-ziel-input"
                              value={s.ziel_ordner ? shortPath(s.ziel_ordner) : ""}
                              tabIndex={-1}
                              onChange={(e) => {
                                // Root-Duplikate beim Einfügen entfernen (kopierte
                                // Pfade enthalten evtl. schon /Holthey Immobilien/)
                                const clean = e.target.value.replace(/^\/+/, "").replace(/^(?:Holthey Immobilien\/)+/i, "");
                                updateZiel(d.path, "/Holthey Immobilien/" + clean);
                              }}
                              placeholder="Ziel-Ordner..."
                              title={s.ziel_ordner || ""}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <button className="v-input-copy-btn" title="Pfad kopieren" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(shortPath(s.ziel_ordner || d.path)); }}><FaCopy /></button>
                          </div>
                          <button className="v-browse-btn" tabIndex={-1} title="Ordner durchsuchen"
                            onClick={(e) => { e.stopPropagation(); setSelectedIdx(idx); openBrowse(d); }}>
                            <FaFolderOpen />
                          </button>
                          </>}
                          <button
                            className={"v-analyse-einzel-btn" + ((analysingPath === d.path || rescanningPath === d.path) ? " loading" : "")}
                            tabIndex={-1}
                            title="Neu analysieren — aus Cache, Alles, OCR-Server oder Gemini wählen"
                            disabled={analysingPath === d.path || rescanningPath === d.path}
                            onClick={(e) => { e.stopPropagation(); setEnginePickerFile({ path: d.path, name: d.name }); }}
                          >
                            <FaSync className={(analysingPath === d.path || rescanningPath === d.path) ? "spin" : ""} />
                          </button>
                          <div className="v-move-group">
                            <button
                              ref={el => { moveBtnRefs.current[d.path] = el; }}
                              tabIndex={isSel ? 0 : -1}
                              className={"v-move-btn" + (!s.ziel_ordner || (conf !== "manuell" && !s.stand && s.zaehlernummer) ? " disabled" : "") + (duplikatChecking ? " checking" : "")}
                              title={conf !== "manuell" && !s.stand && s.zaehlernummer ? "Stand fehlt (Pflichtfeld)" : duplikatChecking ? "Prüfe auf Duplikate..." : modus === "umbenennen" ? "Umbenennen" : "Verschieben"}
                              disabled={moving || duplikatChecking || !s.ziel_ordner || (conf !== "manuell" && !s.stand && !!s.zaehlernummer)}
                              onClick={(e) => { e.stopPropagation(); setSelectedIdx(idx); handleMove(d); }}
                            >
                              {duplikatChecking ? <FaSync className="spin" /> : <FaArrowRight />}
                            </button>
                            <input
                              type="checkbox"
                              className="v-check-batch"
                              title="Für Massenverarbeitung markieren"
                              checked={checkedPaths.has(d.path)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => setCheckedPaths((prev) => {
                                const n = new Set(prev);
                                if (n.has(d.path)) n.delete(d.path); else n.add(d.path);
                                return n;
                              })}
                            />
                          </div>
                          {confirmDeletePath === d.path ? (
                            <>
                              <button className="v-delete-btn confirm" title="Ja, löschen"
                                onClick={(e) => { e.stopPropagation(); handleDelete(d.path); }}>
                                <FaCheck />
                              </button>
                              <button className="v-delete-btn cancel" title="Abbrechen"
                                onClick={(e) => { e.stopPropagation(); setConfirmDeletePath(null); }}>
                                <FaTimes />
                              </button>
                            </>
                          ) : (
                            <button className="v-delete-btn" title="Datei löschen"
                              onClick={(e) => { e.stopPropagation(); setSelectedIdx(idx); setConfirmDeletePath(d.path); }}>
                              <FaTrash />
                            </button>
                          )}
                          <div className="v-row3-separator" />
                          <button
                            className={"v-specific-btn clr-cyan" + (duplicatingPath === d.path ? " loading" : "")}
                            disabled={duplicatingPath === d.path}
                            title="Datei im gleichen Ordner duplizieren"
                            onClick={(e) => { e.stopPropagation(); handleDuplizieren(d.path); }}
                          >
                            {duplicatingPath === d.path ? <FaSync className="spin" /> : <FaCopy />}
                          </button>
                          <button
                            className="v-specific-btn clr-teal"
                            disabled={moving}
                            title="Bild in Fotos/Bilder-Ordner verschieben (kein DB-Eintrag)"
                            onClick={(e) => { e.stopPropagation(); moveToBilder(d); }}
                          >
                            <FaCamera />
                          </button>
                        </div>

                        {modus !== "umbenennen" && (() => {
                            const preview = _buildName(d.path, s);
                            const reason = s.reason || "";
                            return (
                              <div className="v-rename-preview" title={preview || "Noch keine Vorschau"}>
                                {reason === "⏳ OCR läuft…"
                                  ? <span className="v-ocr-pending">⏳ OCR läuft…</span>
                                  : preview
                                    ? <span className="v-preview-name">→ {preview}</span>
                                    : <span className="v-preview-empty">Keine Vorschau</span>
                                }
                              </div>
                            );
                          })()}
                        {s.reason && s.reason !== "⏳ OCR läuft…" && <div className="v-card-reason">{s.reason}</div>}
                      </div>
                    );
                  })}
                </div>
              );
            });
          })()}
          </div>

          {/* Ordner-Fehlt-Modal */}
          {ordnerFehltModal && (
            <div className="v-modal-backdrop" onClick={() => setOrdnerFehltModal(null)}>
              <div className="v-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520, width: "90%" }}>
                <div className="v-modal-header">
                  <h3 style={{ color: "#ef5350" }}>Ordner nicht gefunden</h3>
                  <button className="v-dismiss" onClick={() => setOrdnerFehltModal(null)}>×</button>
                </div>
                <p className="v-modal-desc" style={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: "0.82rem", background: "#1a1a1a", padding: "10px 12px", borderRadius: 4, margin: "12px 0" }}>
                  {ordnerFehltModal.pfad}
                </p>
                <p className="v-modal-desc">Der Ziel-Ordner existiert nicht in Dropbox. Möchtest du ihn manuell auswählen?</p>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
                  <button className="v-btn ghost" onClick={() => setOrdnerFehltModal(null)}>Abbrechen</button>
                  <button className="v-btn primary" onClick={() => {
                    updateZiel(ordnerFehltModal.datei.path, "");
                    openBrowse(ordnerFehltModal.datei);
                    setOrdnerFehltModal(null);
                  }}>Ordner auswählen</button>
                </div>
              </div>
            </div>
          )}

          {/* Ordner-Fehlt-Modal */}
          {ordnerFehltModal && (
            <div className="v-modal-backdrop" onClick={() => setOrdnerFehltModal(null)}>
              <div className="v-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520, width: "90%" }}>
                <div className="v-modal-header">
                  <h3 style={{ color: "#ef5350" }}>Ordner nicht gefunden</h3>
                  <button className="v-dismiss" onClick={() => setOrdnerFehltModal(null)}>×</button>
                </div>
                <p style={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: "0.82rem", background: "#1a1a1a", padding: "10px 12px", borderRadius: 4, margin: "12px 0" }}>
                  {ordnerFehltModal.pfad}
                </p>
                <p className="v-modal-desc">Der Ziel-Ordner existiert nicht in Dropbox. Möchtest du ihn manuell auswählen?</p>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
                  <button className="v-btn ghost" onClick={() => setOrdnerFehltModal(null)}>Abbrechen</button>
                  <button className="v-btn primary" onClick={() => {
                    updateZiel(ordnerFehltModal.datei.path, "");
                    openBrowse(ordnerFehltModal.datei);
                    setOrdnerFehltModal(null);
                  }}>Ordner auswählen</button>
                </div>
              </div>
            </div>
          )}

          {/* Anlegen-Modal: Neuen Zähler im Katalog erstellen */}
          {anlegenModal && (
            <div className="v-modal-backdrop" onClick={() => setAnlegenModal(null)}>
              <div className="v-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440, width: "90%" }}>
                <div className="v-modal-header">
                  <h3>Neuen Zähler anlegen</h3>
                  <button className="v-dismiss" onClick={() => setAnlegenModal(null)}>×</button>
                </div>
                <p className="v-modal-desc">
                  Zählernummer <strong>{anlegenModal.zaehlernummer}</strong> ist nicht im Katalog.
                </p>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const fd = new FormData(e.target);
                  handleKatalogAnlegen({
                    zaehlernummer: anlegenModal.zaehlernummer,
                    zaehler_art: fd.get("zaehler_art") || "",
                    einheit_nr: fd.get("einheit_nr") || "",
                    objekt: (fd.get("einheit_nr") || "").split("-")[0] || "",
                    einbauort: fd.get("einbauort") || "",
                    versorger: fd.get("versorger") || "",
                    bild_pfad: anlegenModal.path,
                  });
                }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 12, color: "#888" }}>Art</span>
                      <select name="zaehler_art" defaultValue="" style={{ padding: "4px 6px" }}>
                        <option value="">—</option>
                        <option value="Strom">Strom</option>
                        <option value="Gas">Gas</option>
                        <option value="Wasser">Wasser</option>
                        <option value="Heizung">Heizung</option>
                        <option value="Öl">Öl</option>
                        <option value="Zwischenzähler">Zwischenzähler</option>
                        <option value="Allgemein">Allgemein</option>
                      </select>
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 12, color: "#888" }}>Einheit (Prefix)</span>
                      <input name="einheit_nr" placeholder="z.B. 019-01" defaultValue={fileSettings[anlegenModal.path]?.prefix || ""} style={{ padding: "4px 6px" }} />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 12, color: "#888" }}>Einbauort</span>
                      <input name="einbauort" placeholder="z.B. Keller" style={{ padding: "4px 6px" }} />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 12, color: "#888" }}>Versorger</span>
                      <input name="versorger" placeholder="z.B. Stadtwerke" style={{ padding: "4px 6px" }} />
                    </label>
                  </div>
                  <div className="v-modal-actions">
                    <button type="submit" className="v-btn primary">Anlegen</button>
                    <button type="button" className="v-btn ghost" onClick={() => setAnlegenModal(null)}>Abbrechen</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Image Viewer */}
        <div className="v-preview">
          {!selected ? (
            <div className="v-preview-empty">
              <FaCamera className="v-preview-empty-icon" />
              <p>Bild auswählen</p>
            </div>
          ) : (
            <>
              {moveResult && (
                <div className={"v-toast " + (moveResult.ok ? "success" : "error")}>
                  {moveResult.ok ? <FaCheck /> : <FaTimes />}
                  <span>{moveResult.ok ? ("→ " + moveResult.msg) : moveResult.msg}</span>
                </div>
              )}
              {browseOpen && (
                <div className="v-browse-panel" style={{ transform: `translate(${browseDrag.x}px, ${browseDrag.y}px)` }}>
                  <div className="v-browse-header" onMouseDown={onBrowseDragStart} style={{ cursor: "move" }} title="Zum Verschieben ziehen">
                    <span className="v-browse-title">Ordner wählen</span>
                    <button className="v-dismiss" onClick={() => setBrowseOpen(false)}>×</button>
                  </div>
                  <div className="v-browse-current-path">
                    <FaFolder className="v-browse-current-icon" />
                    {browsePath ? browsePath.split("/").filter(Boolean).reduce((acc, part) => {
                      const p = (acc.length > 0 ? acc[acc.length - 1].p : "") + "/" + part;
                      acc.push({ name: part, p });
                      return acc;
                    }, []).map((seg, i, arr) => (
                      <span key={seg.p} style={{ display: "inline-flex", alignItems: "center" }}>
                        {i > 0 && <span style={{ color: "#444", margin: "0 2px" }}>/</span>}
                        <button
                          className={"v-crumb" + (i === arr.length - 1 ? " active" : "")}
                          onClick={() => browseFolder(seg.p)}
                        >{seg.name}</button>
                      </span>
                    )) : <span style={{ color: "#555" }}>…</span>}
                  </div>
                  {browseLoading ? (
                    <div className="v-browse-loading">Lade...</div>
                  ) : (
                    <div className="v-browse-items">
                      <button className="v-browse-item pick" onClick={() => pickFolder(browsePath)}>
                        <FaCheck /> Hier ablegen
                      </button>
                      {newFolderMode ? (
                        <div className="v-new-folder-row">
                          <FaFolderPlus className="v-new-folder-icon" />
                          <input
                            className="v-new-folder-input"
                            autoFocus
                            placeholder="Ordner-Name..."
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { createFolder(browsePath, newFolderName, browseFolder); setNewFolderMode(false); setNewFolderName(""); }
                              if (e.key === "Escape") { setNewFolderMode(false); setNewFolderName(""); }
                            }}
                          />
                          <button className="v-btn ghost small" title="Erstellen" onClick={() => { createFolder(browsePath, newFolderName, browseFolder); setNewFolderMode(false); setNewFolderName(""); }}><FaCheck /></button>
                          <button className="v-btn ghost small" title="Abbrechen" onClick={() => { setNewFolderMode(false); setNewFolderName(""); }}><FaTimes /></button>
                        </div>
                      ) : (
                        <button className="v-browse-item new-folder" onClick={() => { setNewFolderMode(true); setNewFolderName(""); }}>
                          <FaFolderPlus /> Neuen Ordner erstellen
                        </button>
                      )}
                      {browseItems.map((item) => (
                        <button key={item.path} className="v-browse-item"
                          onClick={() => browseFolder(item.path)}>
                          <FaFolder /> {item.name}
                        </button>
                      ))}
                      {browseItems.length === 0 && <div className="v-browse-empty">Keine Unterordner</div>}
                    </div>
                  )}
                </div>
              )}
              {/* Image Toolbar: Rotate + Zoom */}
              {selected && (
                <div className="v-img-toolbar">
                  <button className="v-img-tool-btn" title="90° links drehen" onClick={() => setImgRotation(r => (r - 90) % 360)}>
                    <FaUndo />
                  </button>
                  <button className="v-img-tool-btn" title="90° rechts drehen" onClick={() => setImgRotation(r => (r + 90) % 360)}>
                    <FaRedo />
                  </button>
                  <span className="v-img-tool-sep" />
                  <button className="v-img-tool-btn" title="Verkleinern" onClick={() => { setImgZoom(z => Math.max(0.25, z / 1.3)); setImgPan({ x: 0, y: 0 }); }}>
                    <FaSearchMinus />
                  </button>
                  <span className="v-img-zoom-label">{Math.round(imgZoom * 100)}%</span>
                  <button className="v-img-tool-btn" title="Vergrößern" onClick={() => setImgZoom(z => Math.min(10, z * 1.3))}>
                    <FaSearchPlus />
                  </button>
                  <button className="v-img-tool-btn" title="Zurücksetzen" onClick={() => { setImgZoom(1); setImgRotation(0); setImgPan({ x: 0, y: 0 }); }}>
                    <FaCompress />
                  </button>
                  {imgRotation !== 0 && (
                    <>
                      <span className="v-img-tool-sep" />
                      <button
                        className="v-img-tool-btn v-img-save-btn"
                        title="Rotation in Dropbox speichern"
                        onClick={async () => {
                          if (!selected?.path) return;
                          try {
                            await api("/bilder/rotate", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ pfad: selected.path, grad: imgRotation }),
                            });
                            delete thumbCache.current[selected.path];
                            setImgRotation(0);
                            setThumbRefresh(n => n + 1);
                          } catch (e) {
                            alert("Fehler beim Speichern: " + e.message);
                          }
                        }}
                      >
                        <FaSave /> Speichern
                      </button>
                    </>
                  )}
                </div>
              )}
              <div
                className="v-thumb-area"
                onWheel={(e) => {
                  if (!thumbnail) return;
                  e.preventDefault();
                  const delta = e.deltaY < 0 ? 1.15 : 1 / 1.15;
                  setImgZoom(z => Math.min(10, Math.max(0.25, z * delta)));
                }}
                onMouseDown={(e) => {
                  if (imgZoom <= 1 || !thumbnail) return;
                  isPanning.current = true;
                  panStart.current = { x: e.clientX, y: e.clientY };
                  panOffset.current = { ...imgPan };
                  e.preventDefault();
                }}
                onMouseMove={(e) => {
                  if (!isPanning.current) return;
                  setImgPan({
                    x: panOffset.current.x + (e.clientX - panStart.current.x),
                    y: panOffset.current.y + (e.clientY - panStart.current.y),
                  });
                }}
                onMouseUp={() => { isPanning.current = false; }}
                onMouseLeave={() => { isPanning.current = false; }}
              >
                {thumbLoading ? (
                  <div className="v-thumb-loading">
                    <div className="v-pulse-ring small" />
                    <span>Bild laden...</span>
                  </div>
                ) : thumbnail ? (
                  <img
                    src={"data:" + (thumbnail.type || "image/jpeg") + ";base64," + thumbnail.image}
                    alt="Vorschau"
                    className="v-thumb-img bilder-thumb"
                    draggable={false}
                    style={{
                      transform: `rotate(${imgRotation}deg) scale(${imgZoom}) translate(${imgPan.x / imgZoom}px, ${imgPan.y / imgZoom}px)`,
                      transformOrigin: "center center",
                      cursor: imgZoom > 1 ? (isPanning.current ? "grabbing" : "grab") : "default",
                      transition: isPanning.current ? "none" : "transform 0.2s ease",
                    }}
                  />
                ) : (
                  <div className="v-thumb-empty">Keine Vorschau</div>
                )}
              </div>
              {/* Geodaten / GPS-Info */}
              {(geodaten || geodatenLoading) && (
                <div className="v-geodaten-bar">
                  <FaMapMarkerAlt className="v-geodaten-icon" />
                  {geodatenLoading ? (
                    <span className="v-geodaten-loading">GPS-Daten laden…</span>
                  ) : geodaten ? (
                    <div className="v-geodaten-content">
                      <div className="v-geodaten-coords">
                        <span title="Breitengrad">{Number(geodaten.latitude).toFixed(6)}°</span>
                        <span className="v-geodaten-sep">|</span>
                        <span title="Längengrad">{Number(geodaten.longitude).toFixed(6)}°</span>
                        {geodaten.altitude != null && (
                          <><span className="v-geodaten-sep">|</span><span title="Höhe">{Number(geodaten.altitude).toFixed(0)} m</span></>
                        )}
                        <a
                          className="v-geodaten-map-link"
                          href={`https://www.google.com/maps?q=${geodaten.latitude},${geodaten.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="In Google Maps öffnen"
                        >Maps ↗</a>
                        <button
                          className="v-geodaten-config-btn"
                          title="Geodaten-Konfiguration"
                          onClick={() => setGeoConfigOpen(true)}
                          style={{ marginLeft: 8, background: "none", border: "1px solid #555", color: "#aaa", borderRadius: 4, padding: "1px 6px", cursor: "pointer", fontSize: 11 }}
                        >⚙</button>
                      </div>
                      {geodaten.adresse && (
                        <div className="v-geodaten-adresse" title={geodaten.adresse}>
                          {geodaten.adresse}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
              {/* OCR-Rohtexte unter dem Viewer (ausklappbar) */}
              {selected && (() => {
                const s = fileSettings[selected.path];
                const bc  = s?._ocr_text_barcode || "";
                const ez  = s?._ocr_text_easy || "";
                const pd  = s?._ocr_text_paddle || "";
                if (!bc && !ez && !pd) return null;
                const count = [bc, ez, pd].filter(Boolean).length;
                return (
                  <div className="v-ocr-accordion">
                    <button className="v-ocr-accordion-header" onClick={() => setOcrOpen((o) => !o)}>
                      <span className="v-ocr-accordion-title">OCR-Rohtexte</span>
                      <span className="v-ocr-accordion-count">{count} Quelle{count !== 1 ? "n" : ""}</span>
                      <span className={"v-ocr-accordion-chevron" + (ocrOpen ? " open" : "")}>▼</span>
                    </button>
                    {ocrOpen && (
                      <div className="v-ocr-accordion-body">
                        {bc && (
                          <div className="v-ocr-row">
                            <span className="v-ocr-label barcode">Barcode</span>
                            <span className="v-ocr-value">{bc}</span>
                          </div>
                        )}
                        {ez && (
                          <div className="v-ocr-row">
                            <span className="v-ocr-label easy">EasyOCR</span>
                            <span className="v-ocr-value">{ez}</span>
                          </div>
                        )}
                        {pd && (
                          <div className="v-ocr-row">
                            <span className="v-ocr-label paddle">PaddleOCR</span>
                            <span className="v-ocr-value">{pd}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </div>

      {/* Duplikat-Popup Modal */}
      {duplikatInfo && (
        <div className="v-overlay" onClick={() => duplikatInfo.onAbort()}>
          <div className="v-modal v-duplikat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="v-modal-header">
              <h3>⚠️ Duplikat gefunden</h3>
              <button className="v-dismiss" onClick={() => duplikatInfo.onAbort()}>×</button>
            </div>
            <p className="v-modal-desc">
              {duplikatInfo.duplikat_typ === "hash"
                ? "Eine inhaltlich identische Datei existiert bereits im Zielordner."
                : "Im Zielordner existiert bereits eine Datei mit dem gleichen Namen."}
            </p>
            <div className="v-duplikat-details">
              {/* Neue Datei (Quelle) */}
              <div className="v-duplikat-file v-duplikat-source">
                <div className="v-duplikat-label" style={{ color: "#81c784" }}>Neu (wird verschoben)</div>
                <div className="v-duplikat-img-wrap" onClick={() => duplikatQuellThumb && setDuplikatZoom(duplikatQuellThumb.src)} style={{ cursor: duplikatQuellThumb ? "zoom-in" : "default" }}>
                  {duplikatQuellThumb
                    ? <img src={duplikatQuellThumb.src} alt="Neue Datei" />
                    : <div className="v-duplikat-img-placeholder">Lädt…</div>
                  }
                  {duplikatQuellThumb && <div className="v-duplikat-zoom-hint">🔍 Zoomen</div>}
                </div>
                <div className="v-duplikat-name">{duplikatInfo.quell?.name}</div>
                <div className="v-duplikat-meta">
                  {duplikatInfo.quell?.size != null && (
                    <span title="Dateigröße">📁 {(duplikatInfo.quell.size / 1024 / 1024).toFixed(2)} MB</span>
                  )}
                </div>
              </div>
              {/* Bestehende Datei (Ziel) */}
              <div className="v-duplikat-file v-duplikat-existing">
                <div className="v-duplikat-label" style={{ color: "#ef9a9a" }}>Alt (bereits im Zielordner)</div>
                <div className="v-duplikat-img-wrap" onClick={() => duplikatInfo.existierend?.vorschau_url && setDuplikatZoom(duplikatInfo.existierend.vorschau_url)} style={{ cursor: duplikatInfo.existierend?.vorschau_url ? "zoom-in" : "default" }}>
                  {duplikatInfo.existierend?.vorschau_url
                    ? <img src={duplikatInfo.existierend.vorschau_url} alt="Bestehende Datei" />
                    : <div className="v-duplikat-img-placeholder">Keine Vorschau</div>
                  }
                  {duplikatInfo.existierend?.vorschau_url && <div className="v-duplikat-zoom-hint">🔍 Zoomen</div>}
                </div>
                <div className="v-duplikat-name">{duplikatInfo.existierend?.name}</div>
                <div className="v-duplikat-meta">
                  {duplikatInfo.existierend?.size != null && (
                    <span title="Dateigröße">📁 {(duplikatInfo.existierend.size / 1024 / 1024).toFixed(2)} MB</span>
                  )}
                  {duplikatInfo.existierend?.modified && (
                    <span title="Änderungsdatum">📅 {duplikatInfo.existierend.modified.slice(0, 10)}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="v-modal-actions">
              <button className="v-btn danger" onClick={() => duplikatInfo.onReplace()}>
                <FaTrash style={{ marginRight: 6 }} />
                Alte löschen
              </button>
              <button className="v-btn danger" onClick={() => duplikatInfo.onDelete()}>
                <FaTrash style={{ marginRight: 6 }} />
                Neue löschen
              </button>
              <button ref={duplikatReplaceRef} className="v-btn primary" onClick={() => duplikatInfo.onBeideBehalten()}>
                <FaArrowRight style={{ marginRight: 6 }} />
                Beide behalten
              </button>
              <button className="v-btn ghost" onClick={() => duplikatInfo.onAbort()}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zoom-Overlay für Duplikat-Vorschau */}
      {duplikatZoom && (
        <div className="v-duplikat-zoom-overlay" onClick={() => setDuplikatZoom(null)}>
          <button className="v-duplikat-zoom-close" onClick={() => setDuplikatZoom(null)}>×</button>
          <img src={duplikatZoom} alt="Vorschau" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Duplikat-Liste Modal (Batch/Auto) */}
      {duplikatList.length > 0 && (
        <div className="v-overlay" onClick={() => setDuplikatList([])}>
          <div className="v-modal v-duplikat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="v-modal-header">
              <h3>⚠️ {duplikatList.length} Duplikat{duplikatList.length > 1 ? "e" : ""} gefunden</h3>
              <button className="v-dismiss" onClick={() => setDuplikatList([])}>×</button>
            </div>
            <p className="v-modal-desc">
              Folgende Dateien existieren bereits identisch im Zielordner.
              Diese werden übersprungen. Möchtest du die Quelldateien löschen?
            </p>
            <div className="v-batch-list">
              {duplikatList.map((d, i) => (
                <div key={i} className="v-batch-item v-duplikat-batch-item">
                  <span>{d.quell?.name || d.quell?.quell_pfad?.split("/").pop()}</span>
                  <span className="dim">= {d.existierend?.name}</span>
                </div>
              ))}
            </div>
            <div className="v-modal-actions">
              <button className="v-btn danger" onClick={async () => {
                for (const d of duplikatList) {
                  try {
                    const pfad = d.quell?.quell_pfad || d.quell?.path;
                    if (pfad) await apiPost("/bilder/loeschen", { pfad });
                  } catch (e) { console.warn("Löschen fehlgeschlagen:", e); }
                }
                setDateien((prev) => {
                  const delPaths = new Set(duplikatList.map((d) => d.quell?.quell_pfad || d.quell?.path));
                  return prev.filter((f) => !delPaths.has(f.path));
                });
                setScrollTick((t) => t + 1);
                setDuplikatList([]);
              }}>
                <FaTrash style={{ marginRight: 6 }} />
                Alle {duplikatList.length} Quelldateien löschen
              </button>
              <button className="v-btn ghost" onClick={() => setDuplikatList([])}>
                Überspringen (nicht löschen)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Modal */}
      {showBatch && (
        <div className="v-overlay" onClick={() => setShowBatch(false)}>
          <div className="v-modal" onClick={(e) => e.stopPropagation()}>
            <div className="v-modal-header">
              <h3><FaLayerGroup /> {modus === "umbenennen" ? "Batch Umbenennen" : "Batch Verschieben"}</h3>
              <button className="v-dismiss" onClick={() => setShowBatch(false)}>×</button>
            </div>
            <p className="v-modal-desc">{batchToProcess.length} Bilder {modus === "umbenennen" ? "umbenennen" : "verschieben"}{checkedPaths.size > 0 ? " (markierte Auswahl)" : ""}:</p>
            <div className="v-batch-stats">
              <span className="v-tag green">{batchToProcess.filter((d) => fileSettings[d.path]?.confidence === "green").length} sicher</span>
            </div>
            <div className="v-batch-list">
              {batchToProcess.map((d) => {
                const s = fileSettings[d.path] || {};
                return (
                  <div key={d.path} className="v-batch-item">
                    <span>{d.name}</span>
                    <span className="dim">→ {shortPath(s.ziel_ordner)}</span>
                  </div>
                );
              })}
            </div>
            <div className="v-modal-actions">
              <button className="v-btn primary" onClick={handleBatch} disabled={batchMoving}>
                {batchMoving ? (modus === "umbenennen" ? "Benenne um..." : "Verschiebe...") : "Alle " + batchToProcess.length + (modus === "umbenennen" ? " umbenennen" : " verschieben")}
              </button>
              <button className="v-btn ghost" onClick={() => setShowBatch(false)} disabled={batchMoving}>
                Abbrechen
              </button>
            </div>
            {batchResult && (
              <div className={"v-toast " + (batchResult.fehler ? "error" : "success")}>
                {batchResult.fehler
                  ? batchResult.fehler
                  : batchResult.erfolgreich + " ok, " + batchResult.fehlgeschlagen + " fehl"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Geodaten Config Modal ── */}
      {geoConfigOpen && (
        <div className="v-modal-overlay" onClick={() => { setGeoConfigOpen(false); setGeoConfigEdit(null); }}>
          <div className="v-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: "80vh", overflow: "auto" }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 16 }}>Geodaten-Konfiguration</h3>

            {/* Tabelle */}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 12 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #444", textAlign: "left" }}>
                  <th style={{ padding: "4px 6px" }}>Koordinaten</th>
                  <th style={{ padding: "4px 6px" }}>Adresse</th>
                  <th style={{ padding: "4px 6px" }}>Objekt</th>
                  <th style={{ padding: "4px 6px" }}>Prefix</th>
                  <th style={{ padding: "4px 6px" }}>R(m)</th>
                  <th style={{ padding: "4px 6px", width: 70 }}></th>
                </tr>
              </thead>
              <tbody>
                {geoConfigItems.map((g) => (
                  <tr key={g.id} style={{ borderBottom: "1px solid #333" }}>
                    <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>{Number(g.lat).toFixed(4)}, {Number(g.lon).toFixed(4)}</td>
                    <td style={{ padding: "4px 6px" }}>{g.adresse || "–"}</td>
                    <td style={{ padding: "4px 6px" }}>{g.objekt || "–"}</td>
                    <td style={{ padding: "4px 6px" }}>{g.prefix || "–"}</td>
                    <td style={{ padding: "4px 6px" }}>{g.radius_m}</td>
                    <td style={{ padding: "4px 6px", display: "flex", gap: 4 }}>
                      <button className="v-btn ghost" style={{ fontSize: 11, padding: "2px 6px" }}
                        onClick={() => setGeoConfigEdit({ ...g })}
                      >✎</button>
                      <button className="v-btn ghost" style={{ fontSize: 11, padding: "2px 6px", color: "#ef5350" }}
                        onClick={async () => { if (window.confirm("Eintrag löschen?")) { await deleteGeoConfig(g.id); }}}
                      >✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Neu / Edit Form */}
            <div style={{ borderTop: "1px solid #444", paddingTop: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                {geoConfigEdit?.id ? "Bearbeiten" : "Neuer Standort"}
                {geodaten && !geoConfigEdit && (
                  <button className="v-btn ghost" style={{ marginLeft: 8, fontSize: 11 }}
                    onClick={() => setGeoConfigEdit({
                      lat: geodaten.latitude, lon: geodaten.longitude, radius_m: 200,
                      adresse: geodaten.adresse || "", objekt: "", prefix: "", pfad: "", bemerkung: "",
                    })}
                  >← Aktuelle GPS-Daten übernehmen</button>
                )}
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                const item = {
                  id: geoConfigEdit?.id || undefined,
                  lat: parseFloat(fd.get("lat")),
                  lon: parseFloat(fd.get("lon")),
                  radius_m: parseInt(fd.get("radius_m") || "200", 10),
                  adresse: fd.get("adresse") || "",
                  objekt: fd.get("objekt") || "",
                  prefix: fd.get("prefix") || "",
                  pfad: fd.get("pfad") || "",
                  bemerkung: fd.get("bemerkung") || "",
                };
                await saveGeoConfig(item);
                setGeoConfigEdit(null);
              }} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 11, color: "#888" }}>Breitengrad</span>
                  <input name="lat" type="number" step="any" required defaultValue={geoConfigEdit?.lat || ""} style={{ padding: "3px 6px" }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 11, color: "#888" }}>Längengrad</span>
                  <input name="lon" type="number" step="any" required defaultValue={geoConfigEdit?.lon || ""} style={{ padding: "3px 6px" }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 11, color: "#888" }}>Adresse</span>
                  <input name="adresse" defaultValue={geoConfigEdit?.adresse || ""} style={{ padding: "3px 6px" }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 11, color: "#888" }}>Radius (m)</span>
                  <input name="radius_m" type="number" defaultValue={geoConfigEdit?.radius_m || 200} style={{ padding: "3px 6px" }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 11, color: "#888" }}>Objekt</span>
                  <input name="objekt" defaultValue={geoConfigEdit?.objekt || ""} placeholder="z.B. 000, 101" style={{ padding: "3px 6px" }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 11, color: "#888" }}>Prefix</span>
                  <input name="prefix" defaultValue={geoConfigEdit?.prefix || ""} placeholder="z.B. 000-Allg (leer = Dropdown)" style={{ padding: "3px 6px" }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 2, gridColumn: "1 / -1" }}>
                  <span style={{ fontSize: 11, color: "#888" }}>Dropbox-Zielpfad</span>
                  <input name="pfad" defaultValue={geoConfigEdit?.pfad || ""} placeholder="/Holthey Immobilien/... (leer = automatisch)" style={{ padding: "3px 6px", fontFamily: "monospace", fontSize: 11 }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 2, gridColumn: "1 / -1" }}>
                  <span style={{ fontSize: 11, color: "#888" }}>Bemerkung</span>
                  <input name="bemerkung" defaultValue={geoConfigEdit?.bemerkung || ""} style={{ padding: "3px 6px" }} />
                </label>
                <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
                  <button type="submit" className="v-btn primary" style={{ fontSize: 12 }}>
                    {geoConfigEdit?.id ? "Speichern" : "Hinzufügen"}
                  </button>
                  {geoConfigEdit && (
                    <button type="button" className="v-btn ghost" style={{ fontSize: 12 }} onClick={() => setGeoConfigEdit(null)}>Abbrechen</button>
                  )}
                </div>
              </form>
            </div>

            <div style={{ marginTop: 12, textAlign: "right" }}>
              <button className="v-btn ghost" onClick={() => { setGeoConfigOpen(false); setGeoConfigEdit(null); }}>Schließen</button>
            </div>
          </div>
        </div>
      )}
      {/* NOK-Picker Popup */}
      {nokPickerFile && (
        <div className="v-pipeline-overlay" onClick={() => setNokPickerFile(null)}>
          <div className="v-pipeline-modal" onClick={e => e.stopPropagation()}>
            <h3>NOK — Art wählen</h3>
            <p className="v-pipeline-subtitle">Was soll mit der Datei passieren?</p>
            <div className="v-pipeline-options">
              <button
                className="v-pipeline-btn v-nok-kopie"
                onClick={() => {
                  setFileSettings(prev => ({ ...prev, [nokPickerFile.path]: { ...prev[nokPickerFile.path], kopie_nok: true, nok_original: false } }));
                  setNokPickerFile(null);
                }}
              >
                <span className="v-pipeline-label">Kopie nach NOK</span>
                <span className="v-pipeline-desc">Original → Zielordner<br/>Kopie → NOK</span>
              </button>
              <button
                className="v-pipeline-btn v-nok-original"
                onClick={() => {
                  const f = nokPickerFile;
                  setNokPickerFile(null);
                  moveNokOriginal(f);
                }}
              >
                <span className="v-pipeline-label">Original → NOK</span>
                <span className="v-pipeline-desc">Direkt in NOK<br/>verschieben</span>
              </button>
            </div>
            <button className="v-pipeline-cancel" onClick={() => setNokPickerFile(null)}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Trello Popup */}
      {trelloPopupOpen && trelloPopupPath && (() => {
        const tDatei = dateien.find(d => d.path === trelloPopupPath);
        const tSett = fileSettings[trelloPopupPath] || {};
        const tFilename = tDatei?.name || trelloPopupPath.split("/").pop();
        const tZiel = tSett.ziel_ordner || "";
        const tVorschau = [
          `**Datei:** ${tFilename}`,
          `**Quell-Pfad:** ${trelloPopupPath}`,
          tZiel ? `**Ziel-Ordner:** ${tZiel}` : null,
          trelloGrund ? `\n**Grund / Anmerkung:**\n${trelloGrund}` : null,
          `\n*Erstellt via IMHO Zählerstand*`,
        ].filter(Boolean).join("\n");
        return (
          <div className="v-overlay" onClick={() => { if (!trelloSaving) setTrelloPopupOpen(false); }}>
            <div className="v-trello-modal v-modal" onClick={e => e.stopPropagation()}>
              <div className="v-modal-header">
                <h3 style={{ color: "#9c27b0" }}>🟣 Trello-Card erstellen</h3>
                <button className="v-dismiss" onClick={() => setTrelloPopupOpen(false)}>&#215;</button>
              </div>
              <div className="v-trello-body">
                <label className="v-strg-field">
                  <span>Card-Titel</span>
                  <input
                    value={trelloTitel}
                    onChange={e => setTrelloTitel(e.target.value)}
                    placeholder={tFilename}
                  />
                </label>
                <label className="v-strg-field">
                  <span>Dropbox-Pfad</span>
                  <input readOnly value={trelloPopupPath} style={{ opacity: 0.6, fontSize: "0.75rem" }} />
                </label>
                <label className="v-strg-field">
                  <span>Warum / Anmerkung</span>
                  <textarea
                    rows={3}
                    placeholder="z.B. Zähler nicht erkannt, Stand prüfen, ..."
                    value={trelloGrund}
                    onChange={e => {
                      const newGrund = e.target.value;
                      setTrelloGrund(newGrund);
                      const base = cleanFilenameForTrello(tFilename);
                      if (trelloTitel === base || trelloTitel === buildTrelloTitle(tFilename, trelloGrund)) {
                        setTrelloTitel(buildTrelloTitle(tFilename, newGrund));
                      }
                    }}
                    style={{ resize: "vertical", minHeight: 64 }}
                  />
                </label>
                <div className="v-trello-preview">
                  <div className="v-trello-preview-label">Vorschau Beschreibung:</div>
                  <pre className="v-trello-preview-text">{tVorschau}</pre>
                </div>
                {trelloError && <div className="v-strg-error">{trelloError}</div>}
                {trelloSuccess && <div className="v-strg-success">{trelloSuccess}</div>}
              </div>
              <div className="v-modal-actions">
                <button
                  ref={trelloBeimVerschiebenRef}
                  className="v-btn primary"
                  style={{ background: "#9c27b0", borderColor: "#9c27b0" }}
                  disabled={trelloSaving}
                  title="Card wird erst beim Verschieben erstellt"
                  onClick={() => {
                    setFileSettings(prev => ({
                      ...prev,
                      [trelloPopupPath]: { ...prev[trelloPopupPath], trello_enabled: true, trello_mode: "beim_verschieben", trello_grund: trelloGrund, trello_titel: trelloTitel },
                    }));
                    setTrelloPopupOpen(false);
                  }}
                >
                  📋 Card beim Verschieben
                </button>
                <button
                  className="v-btn"
                  style={{ borderColor: "#9c27b0", color: "#9c27b0" }}
                  disabled={trelloSaving}
                  title="Card jetzt sofort auf Trello erstellen"
                  onClick={async () => {
                    const res = await trelloCreateCard(trelloPopupPath, "direkt", trelloGrund, null, trelloTitel);
                    if (res) setTimeout(() => setTrelloPopupOpen(false), 1200);
                  }}
                >
                  {trelloSaving ? "Erstelle..." : "⚡ Direkt Card erstellen"}
                </button>
                <button className="v-btn ghost" disabled={trelloSaving} onClick={() => setTrelloPopupOpen(false)}>Abbrechen</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Welle 80: gemeinsamer Engine-Picker (Cache / Alles / OCR / Gemini) */}
      {enginePickerFile && (
        <EnginePicker
          file={enginePickerFile}
          title="Analyse-Engine wählen"
          options={[
            { key: "cache", label: "Aus Cache", desc: "nur Zuordnung/Ziel neu berechnen", accent: "cache" },
            { key: "alle", label: "Alles (Standard)", desc: "Metadaten + OCR-Server + Gemini, zusammengeführt", accent: "alle",
              disabled: !geminiStatus?.key_vorhanden, title: !geminiStatus?.key_vorhanden ? "Gemini nicht konfiguriert — nur OCR möglich" : undefined },
            { key: "ocr", label: "Pipeline 1", desc: "OCR-Server (Barcode + OCR) + Metadaten", accent: "v1" },
            { key: "gemini", label: "Pipeline 2", desc: `✨ Gemini (Bild direkt) + Metadaten${enginePickerFile.alle ? " — Tokens je Datei" : ""}`, accent: "v2",
              disabled: !geminiStatus?.key_vorhanden, title: !geminiStatus?.key_vorhanden ? "Gemini nicht konfiguriert (kein API-Key)" : undefined },
          ]}
          onChoose={(key) => runEngine(key, enginePickerFile)}
          onClose={() => setEnginePickerFile(null)}
        />
      )}
      {analyseJobLaeuft && (
        <div className="v-analyse-job-bar" title={analyseJobFortschritt.current}>
          ⏳ Analyse läuft: Bild {analyseJobFortschritt.done}/{analyseJobFortschritt.total}{analyseJobFortschritt.current ? ` — ${analyseJobFortschritt.current}` : ""}
        </div>
      )}

      {/* Hintergrund-Verschiebe-Jobs: Fortschritt + Ergebnis pro Datei */}
      <VerschiebenJobIndicator jobs={jobs} onDismiss={dismissJob} vorlauf={jobVorlauf} />
    </div>
  );
}

export default BilderVerschiebenPage;
